import React from 'react'
import styles from './StorePriceListBulkAction.module.css'
import {
    Download,
    Save,
    UserRound,
    Sparkles,
    Package,
    ShoppingBag,
    Layers3,
    Square,
    CheckSquare,
    MinusSquare,
    TrendingUp,
    BadgePercent,
    Clock3,
    FolderTree,
    Eye,
    FileText,
    Trash2,
    FileDown,
    BarChart3,
    Type,
    Wand2,
    Search,
    ChevronDown,
    X,
} from 'lucide-react'
import Select from 'react-select'
import { BASE_URL_API } from '../../References/URLs'
import AuthContext from '../../store/auth-context'
import ActiveStoreContext from '../../store/ActiveStoreContext'

const ROUNDING_PRESETS = ['1', '5', '10', '50', '100', '500']
const DISCOUNT_PRESETS = ['5', '10', '15', '20', '25', '30', '40', '50']
const DURATION_PRESETS = ['15', '20', '30', '45', '60', '75', '90', '120']
const ROUNDING_MODE_SUMMARY = {
    nearest: 'to the nearest',
    up: 'up to the next',
    down: 'down to the previous',
}
const HSN_CODE_KEYS = ['code', 'hsnCode', 'hsn_code', 'hsnSacCode', 'hsn_sac_code', 'hsn', 'HSN', 'value', 'label']
const HSN_DESCRIPTION_KEYS = ['description', 'desc', 'details', 'detail', 'name', 'label', 'title']
const NAME_STANDARDIZE_CASES = [
    { id: 'title', label: 'Title Case' },
    { id: 'sentence', label: 'Sentence case' },
    { id: 'upper', label: 'UPPERCASE' },
    { id: 'lower', label: 'lowercase' },
    { id: 'preserve', label: 'Keep casing' },
]
const NAME_CASE_LABELS = {
    title: 'Title Case',
    sentence: 'Sentence case',
    upper: 'UPPERCASE',
    lower: 'lowercase',
    preserve: 'Current casing',
}

const EDITABLE_FIELDS = ['serviceName', 'basePrice', 'serviceTime', 'hsnCode', 'offerPercentage', 'description']

const FIELD_LABELS = {
    serviceName: 'Service Name',
    basePrice: 'Price (₹)',
    serviceTime: 'Duration (min)',
    hsnCode: 'HSN Code',
    offerPercentage: 'Offer %',
    description: 'Description',
}

const NUMERIC_FIELDS = new Set(['basePrice', 'serviceTime', 'offerPercentage'])

const normalizeComparisonValue = (field, value) => {
    if (value === undefined || value === null) {
        return ''
    }

    if (NUMERIC_FIELDS.has(field)) {
        const numeric = Number(value)
        if (Number.isFinite(numeric)) {
            return numeric.toString()
        }
        return ''
    }

    return String(value)
}

const formatDisplayValue = (field, value) => {
    if (value === undefined || value === null || value === '') {
        return '—'
    }

    if (NUMERIC_FIELDS.has(field)) {
        const numeric = Number(value)
        if (Number.isFinite(numeric)) {
            if (field === 'basePrice') {
                return `₹${numeric.toLocaleString('en-IN')}`
            }
            if (field === 'offerPercentage') {
                return `${numeric}%`
            }
            if (field === 'serviceTime') {
                return `${numeric} min`
            }
            return numeric.toString()
        }
        return '—'
    }

    if (typeof value === 'string') {
        return value.trim() === '' ? '—' : value
    }

    return String(value)
}

const coerceFieldValueForPersist = (field, value) => {
    if (NUMERIC_FIELDS.has(field)) {
        if (value === undefined || value === null || value === '') {
            return ''
        }
        const numeric = Number(value)
        return Number.isFinite(numeric) ? numeric : ''
    }

    if (value === undefined || value === null) {
        return ''
    }

    return typeof value === 'string' ? value : String(value)
}

const StorePriceListBulkAction = () => {
    const authCtx = React.useContext(AuthContext)
    const asCtx = React.useContext(ActiveStoreContext)
    const activeStore = (() => { 
        try { 
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== "") 
                ? JSON.parse(asCtx.activeStore) 
                : null 
        } catch { 
            return null 
        } 
    })()

    const [priceList, setPriceList] = React.useState([])
    const [loading, setLoading] = React.useState(true)
    const [editedServices, setEditedServices] = React.useState({})
    const [selectedServices, setSelectedServices] = React.useState(new Set())
    const [isBulkActionsOpen, setBulkActionsOpen] = React.useState(false)
    const [activeActionId, setActiveActionId] = React.useState(null)
    const [expandedGroups, setExpandedGroups] = React.useState(() => new Set(['pricing']))
    const [actionSearchTerm, setActionSearchTerm] = React.useState('')
    const [priceAdjustmentMode, setPriceAdjustmentMode] = React.useState('percentage')
    const [priceAdjustmentValue, setPriceAdjustmentValue] = React.useState('')
    const [priceAdjustmentError, setPriceAdjustmentError] = React.useState('')
    const [priceAdjustmentSummary, setPriceAdjustmentSummary] = React.useState(null)
    const [roundingStep, setRoundingStep] = React.useState('10')
    const [roundingMode, setRoundingMode] = React.useState('nearest')
    const [roundingError, setRoundingError] = React.useState('')
    const [roundingSummary, setRoundingSummary] = React.useState(null)
    const [durationValue, setDurationValue] = React.useState('')
    const [durationError, setDurationError] = React.useState('')
    const [durationSummary, setDurationSummary] = React.useState(null)
    const [durationIncreaseValue, setDurationIncreaseValue] = React.useState('')
    const [durationIncreaseError, setDurationIncreaseError] = React.useState('')
    const [durationIncreaseSummary, setDurationIncreaseSummary] = React.useState(null)
    const [durationDecreaseValue, setDurationDecreaseValue] = React.useState('')
    const [durationDecreaseError, setDurationDecreaseError] = React.useState('')
    const [durationDecreaseSummary, setDurationDecreaseSummary] = React.useState(null)
    const [nameStandardizeCase, setNameStandardizeCase] = React.useState('title')
    const [nameStandardizeTrimEdges, setNameStandardizeTrimEdges] = React.useState(true)
    const [nameStandardizeCollapseSpaces, setNameStandardizeCollapseSpaces] = React.useState(true)
    const [nameStandardizeError, setNameStandardizeError] = React.useState('')
    const [nameStandardizeSummary, setNameStandardizeSummary] = React.useState(null)
    const [nameAffixPrefix, setNameAffixPrefix] = React.useState('')
    const [nameAffixSuffix, setNameAffixSuffix] = React.useState('')
    const [nameAffixError, setNameAffixError] = React.useState('')
    const [nameAffixSummary, setNameAffixSummary] = React.useState(null)
    const [nameReplaceFindValue, setNameReplaceFindValue] = React.useState('')
    const [nameReplaceReplaceValue, setNameReplaceReplaceValue] = React.useState('')
    const [nameReplaceCaseSensitive, setNameReplaceCaseSensitive] = React.useState(false)
    const [nameReplaceError, setNameReplaceError] = React.useState('')
    const [nameReplaceSummary, setNameReplaceSummary] = React.useState(null)
    const [duplicateCaseSensitive, setDuplicateCaseSensitive] = React.useState(false)
    const [duplicateTrimWhitespace, setDuplicateTrimWhitespace] = React.useState(true)
    const [duplicateSelectionOnly, setDuplicateSelectionOnly] = React.useState(false)
    const [duplicateSummary, setDuplicateSummary] = React.useState(null)
    const [duplicateError, setDuplicateError] = React.useState('')
    const [discountValue, setDiscountValue] = React.useState('')
    const [discountError, setDiscountError] = React.useState('')
    const [discountSummary, setDiscountSummary] = React.useState(null)
    const [discountRemoveError, setDiscountRemoveError] = React.useState('')
    const [discountRemoveSummary, setDiscountRemoveSummary] = React.useState(null)
    const [hsnCodes, setHsnCodes] = React.useState([])
    const [isLoadingHsnCodes, setIsLoadingHsnCodes] = React.useState(false)
    const [selectedHsnCode, setSelectedHsnCode] = React.useState('')
    const [hsnUpdateError, setHsnUpdateError] = React.useState('')
    const [hsnUpdateSummary, setHsnUpdateSummary] = React.useState(null)
    const [hsnClearError, setHsnClearError] = React.useState('')
    const [hsnClearSummary, setHsnClearSummary] = React.useState(null)
    const [isSavePreviewOpen, setSavePreviewOpen] = React.useState(false)
    const [isSubmittingChanges, setIsSubmittingChanges] = React.useState(false)
    const [saveFeedback, setSaveFeedback] = React.useState(null)
    const [savePreviewError, setSavePreviewError] = React.useState('')

    React.useEffect(() => {
        getPriceList()
    }, [activeStore?.storeIdentifier])

    React.useEffect(() => {
        if (!isBulkActionsOpen) {
            return
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setBulkActionsOpen(false)
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [isBulkActionsOpen])

    React.useEffect(() => {
        if (!isBulkActionsOpen) {
            setActiveActionId(null)
        }
    }, [isBulkActionsOpen])

    React.useEffect(() => {
        if (!isSavePreviewOpen) {
            return
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && !isSubmittingChanges) {
                event.preventDefault()
                setSavePreviewOpen(false)
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [isSavePreviewOpen, isSubmittingChanges])

    React.useEffect(() => {
        if (!saveFeedback || saveFeedback.type === 'error') {
            return
        }

        const timer = window.setTimeout(() => setSaveFeedback(null), 6000)
        return () => window.clearTimeout(timer)
    }, [saveFeedback])

    const getCategoryIcon = React.useCallback((name) => {
        switch ((name || '').toLowerCase()) {
            case 'men':
                return UserRound
            case 'women':
                return UserRound
            case 'packages':
                return Package
            case 'services':
                return Sparkles
            case 'products':
                return ShoppingBag
            default:
                return Layers3
        }
    }, [])

    const bulkActionGroups = React.useMemo(() => ([
        {
            id: 'pricing',
            title: 'Pricing Actions',
            icon: TrendingUp,
            actions: [
                { id: 'price-increase', label: 'Bulk Price Increase', description: 'Raise prices by value or %', requiresSelection: true },
                { id: 'price-decrease', label: 'Bulk Price Decrease', description: 'Lower prices by value or %', requiresSelection: true },
                { id: 'price-round', label: 'Round Prices', description: 'Round to nearest interval', requiresSelection: true },
                { id: 'price-minimum', label: 'Set Minimum Price', description: 'Enforce lower bound', requiresSelection: true },
                { id: 'price-maximum', label: 'Set Maximum Price', description: 'Cap prices', requiresSelection: true },
                { id: 'price-copy', label: 'Copy Prices From…', description: 'Mirror another category', requiresSelection: true },
            ],
        },
        {
            id: 'discounts',
            title: 'Discount / Offer',
            icon: BadgePercent,
            actions: [
                { id: 'discount-apply', label: 'Apply Discount', description: 'Set offer %', requiresSelection: true },
                { id: 'discount-remove', label: 'Remove Discount', description: 'Clear offers', requiresSelection: true },
                { id: 'discount-seasonal', label: 'Seasonal Pricing', description: 'Event-based pricing', requiresSelection: true },
                { id: 'discount-happy-hours', label: 'Happy Hours', description: 'Time-based discount', requiresSelection: true },
            ],
        },
        {
            id: 'duration',
            title: 'Duration Management',
            icon: Clock3,
            actions: [
                { id: 'duration-set', label: 'Set Duration', description: 'Bulk service time', requiresSelection: true },
                { id: 'duration-increase', label: 'Increase Duration', description: 'Add minutes', requiresSelection: true },
                { id: 'duration-decrease', label: 'Decrease Duration', description: 'Reduce minutes', requiresSelection: true },
                { id: 'duration-standardize', label: 'Standardize Duration', description: 'Snap to intervals', requiresSelection: true },
            ],
        },
        {
            id: 'category',
            title: 'Category Actions',
            icon: FolderTree,
            actions: [
                { id: 'category-move', label: 'Move to Category', description: 'Reassign category', requiresSelection: true },
                { id: 'category-duplicate', label: 'Duplicate to Category', description: 'Copy services', requiresSelection: true },
                { id: 'category-merge', label: 'Merge Categories', description: 'Combine categories', requiresSelection: true },
            ],
        },
        {
            id: 'visibility',
            title: 'Visibility / Status',
            icon: Eye,
            actions: [
                { id: 'visibility-enable', label: 'Enable Services', description: 'Make visible', requiresSelection: true },
                { id: 'visibility-disable', label: 'Disable Services', description: 'Hide services', requiresSelection: true },
                { id: 'visibility-popular', label: 'Mark as Popular', description: 'Feature selected', requiresSelection: true },
                { id: 'visibility-gender', label: 'Set Gender', description: 'Assign Men/Women/Unisex', requiresSelection: true },
            ],
        },
        {
            id: 'information',
            title: 'Information Updates',
            icon: FileText,
            actions: [
                { id: 'info-description', label: 'Bulk Add Description', description: 'Template content', requiresSelection: true },
                { id: 'info-hsn', label: 'Bulk Update HSN Code', description: 'Apply tax codes', requiresSelection: true },
                { id: 'info-tags', label: 'Add Service Tags', description: 'Categorize services', requiresSelection: true },
                { id: 'info-images', label: 'Set Service Images', description: 'Upload imagery', requiresSelection: true },
            ],
        },
        {
            id: 'removal',
            title: 'Removal / Archive',
            icon: Trash2,
            actions: [
                { id: 'removal-delete', label: 'Delete Selected', description: 'Permanent removal', requiresSelection: true },
                { id: 'removal-archive', label: 'Archive Services', description: 'Soft delete', requiresSelection: true },
                { id: 'removal-clear-hsn', label: 'Clear HSN Codes', description: 'Remove tax codes', requiresSelection: true },
                { id: 'removal-clear-desc', label: 'Clear Descriptions', description: 'Reset descriptions', requiresSelection: true },
            ],
        },
        {
            id: 'export',
            title: 'Export / Import',
            icon: FileDown,
            actions: [
                { id: 'export-excel', label: 'Export to Excel', description: 'Spreadsheet download', requiresSelection: false },
                { id: 'export-pdf', label: 'Export to PDF', description: 'Generate PDF', requiresSelection: false },
                { id: 'import-excel', label: 'Import from Excel', description: 'Bulk upload', requiresSelection: false },
                { id: 'export-template', label: 'Export as Template', description: 'Blank template', requiresSelection: false },
            ],
        },
        {
            id: 'advanced',
            title: 'Advanced Actions',
            icon: Wand2,
            actions: [
                { id: 'advanced-clone', label: 'Clone Services', description: 'Duplicate selected', requiresSelection: true },
                { id: 'advanced-template', label: 'Apply Template', description: 'Predefined pricing', requiresSelection: true },
                { id: 'advanced-compare', label: 'Compare Prices', description: 'Benchmarking', requiresSelection: true },
                { id: 'advanced-history', label: 'Price History', description: 'Review & revert', requiresSelection: true },
                { id: 'advanced-sync', label: 'Sync Across Stores', description: 'Copy to branches', requiresSelection: true },
            ],
        },
        {
            id: 'analysis',
            title: 'Analysis Tools',
            icon: BarChart3,
            actions: [
                { id: 'analysis-revenue', label: 'Calculate Revenue', description: 'Estimate potential', requiresSelection: true },
                { id: 'analysis-sort', label: 'Sort by Price', description: 'Reorder services', requiresSelection: false },
                { id: 'analysis-filter', label: 'Filter by Range', description: 'Show price range', requiresSelection: false },
                { id: 'analysis-duplicates', label: 'Find Duplicates', description: 'Identify similar names', requiresSelection: false },
            ],
        },
        {
            id: 'formatting',
            title: 'Formatting Tools',
            icon: Type,
            actions: [
                { id: 'format-standardize', label: 'Standardize Names', description: 'Clean casing/spaces', requiresSelection: true },
                { id: 'format-prefix-suffix', label: 'Add Prefix/Suffix', description: 'Append text', requiresSelection: true },
                { id: 'format-replace', label: 'Replace Text', description: 'Find & replace', requiresSelection: true },
            ],
        },
    ]), [])

    const bulkActionDictionary = React.useMemo(() => {
        const map = {}
        bulkActionGroups.forEach(group => {
            group.actions.forEach(action => {
                map[action.id] = {
                    ...action,
                    groupId: group.id,
                    groupTitle: group.title,
                }
            })
        })
        return map
    }, [bulkActionGroups])

    const filteredActionGroups = React.useMemo(() => {
        const term = actionSearchTerm.trim().toLowerCase()
        if (!term) {
            return bulkActionGroups
        }

        return bulkActionGroups
            .map(group => {
                const matchingActions = group.actions.filter(action => {
                    const label = (action.label || '').toLowerCase()
                    const description = (action.description || '').toLowerCase()
                    return label.includes(term) || description.includes(term)
                })
                if (matchingActions.length === 0) {
                    return null
                }
                return {
                    ...group,
                    actions: matchingActions,
                }
            })
            .filter(Boolean)
    }, [bulkActionGroups, actionSearchTerm])

            const hasActionSearch = actionSearchTerm.trim().length > 0

            React.useEffect(() => {
                if (!hasActionSearch) {
                    return
                }

                setExpandedGroups((prev) => {
                    let updated = false
                    const next = new Set(prev)
                    filteredActionGroups.forEach(group => {
                        if (!next.has(group.id)) {
                            next.add(group.id)
                            updated = true
                        }
                    })
                    return updated ? next : prev
                })
            }, [hasActionSearch, filteredActionGroups])

    const implementedActionIds = React.useMemo(() => new Set([
        'price-increase',
        'price-decrease',
        'price-round',
        'duration-set',
        'duration-increase',
        'duration-decrease',
        'format-standardize',
        'format-prefix-suffix',
        'format-replace',
        'discount-apply',
        'discount-remove',
        'info-hsn',
        'removal-clear-hsn',
        'analysis-duplicates',
    ]), [])

    const activeAction = activeActionId ? bulkActionDictionary[activeActionId] : null
    const isPriceAdjustmentAction = activeActionId === 'price-increase' || activeActionId === 'price-decrease'
    const isRoundingAction = activeActionId === 'price-round'
    const isDurationSetAction = activeActionId === 'duration-set'
    const isDurationIncreaseAction = activeActionId === 'duration-increase'
    const isDurationDecreaseAction = activeActionId === 'duration-decrease'
    const isNameStandardizeAction = activeActionId === 'format-standardize'
    const isNameAffixAction = activeActionId === 'format-prefix-suffix'
    const isNameReplaceAction = activeActionId === 'format-replace'
    const isDuplicateAnalysisAction = activeActionId === 'analysis-duplicates'
    const isDiscountApplyAction = activeActionId === 'discount-apply'
    const isDiscountRemoveAction = activeActionId === 'discount-remove'
    const isHsnBulkUpdateAction = activeActionId === 'info-hsn'
    const isHsnClearAction = activeActionId === 'removal-clear-hsn'

    React.useEffect(() => {
        setPriceAdjustmentMode('percentage')
        setPriceAdjustmentValue('')
        setPriceAdjustmentError('')
        setPriceAdjustmentSummary(null)
        setRoundingStep('10')
        setRoundingMode('nearest')
        setRoundingError('')
        setRoundingSummary(null)
        setDurationValue('')
        setDurationError('')
        setDurationSummary(null)
        setDurationIncreaseValue('')
        setDurationIncreaseError('')
        setDurationIncreaseSummary(null)
        setDurationDecreaseValue('')
        setDurationDecreaseError('')
        setDurationDecreaseSummary(null)
        setNameStandardizeCase('title')
        setNameStandardizeTrimEdges(true)
        setNameStandardizeCollapseSpaces(true)
        setNameStandardizeError('')
        setNameStandardizeSummary(null)
        setNameAffixPrefix('')
        setNameAffixSuffix('')
        setNameAffixError('')
        setNameAffixSummary(null)
        setNameReplaceFindValue('')
        setNameReplaceReplaceValue('')
        setNameReplaceCaseSensitive(false)
        setNameReplaceError('')
        setNameReplaceSummary(null)
        setDuplicateCaseSensitive(false)
        setDuplicateTrimWhitespace(true)
        setDuplicateSelectionOnly(false)
        setDuplicateSummary(null)
        setDuplicateError('')
        setDiscountValue('')
        setDiscountError('')
        setDiscountSummary(null)
        setDiscountRemoveError('')
        setDiscountRemoveSummary(null)
        setSelectedHsnCode('')
        setHsnUpdateError('')
        setHsnUpdateSummary(null)
        setHsnClearError('')
        setHsnClearSummary(null)
    }, [activeActionId])

    const selectionFingerprint = React.useMemo(() => {
        if (selectedServices.size === 0) {
            return 'none'
        }
        return Array.from(selectedServices).sort().join('|')
    }, [selectedServices])

    React.useEffect(() => {
        if (!selectedHsnCode) {
            return
        }
        const stillExists = hsnCodes.some(entry => entry.code === selectedHsnCode)
        if (!stillExists) {
            setSelectedHsnCode('')
        }
    }, [hsnCodes, selectedHsnCode])

    React.useEffect(() => {
        setPriceAdjustmentSummary(null)
        setPriceAdjustmentError('')
        setRoundingSummary(null)
        setRoundingError('')
        setDurationSummary(null)
        setDurationError('')
        setDurationIncreaseSummary(null)
        setDurationIncreaseError('')
        setDurationDecreaseSummary(null)
        setDurationDecreaseError('')
        setNameStandardizeSummary(null)
        setNameStandardizeError('')
        setNameAffixSummary(null)
        setNameAffixError('')
        setNameReplaceSummary(null)
        setNameReplaceError('')
        setDuplicateSummary(null)
        setDuplicateError('')
        if (selectedServices.size === 0 && duplicateSelectionOnly) {
            setDuplicateSelectionOnly(false)
        }
        setDiscountSummary(null)
        setDiscountError('')
        setDiscountRemoveSummary(null)
        setDiscountRemoveError('')
        setHsnUpdateSummary(null)
        setHsnUpdateError('')
        setHsnClearSummary(null)
        setHsnClearError('')
    }, [selectionFingerprint, duplicateSelectionOnly, selectedServices.size])

    const getPriceList = async () => {
        setLoading(true)
        try {
            const response = await fetch(BASE_URL_API + "getPriceList", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore?.storeIdentifier
                })
            })

            if (!response.ok) {
                console.log("Something went wrong : Server Error")
            } else {
                const respo = await response.json()
                if (respo?.status === "success") {
                    if (Array.isArray(respo?.response)) {
                        setPriceList(respo?.response)
                    }
                } else {
                    if(respo?.message === "tokenExpired") {
                        authCtx.logout()
                    } else {
                        console.log(respo)
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching price list:", error)
        } finally {
            setLoading(false)
        }
    }

    // Count total services
    const totalServices = React.useMemo(() => {
        return priceList.reduce((acc, category) => {
            return acc + (category.services?.length || 0)
        }, 0)
    }, [priceList])

    // Count categories with services
    const activeCategories = React.useMemo(() => {
        return priceList.filter(category => category.services?.length > 0).length
    }, [priceList])

    const serviceLookup = React.useMemo(() => {
        const map = new Map()
        priceList.forEach(category => {
            category.services?.forEach(service => {
                map.set(service.identifier, {
                    service,
                    category,
                })
            })
        })
        return map
    }, [priceList])

    const savePreviewDetails = React.useMemo(() => {
        const entries = []
        let totalFieldChanges = 0

        Object.entries(editedServices || {}).forEach(([serviceId, overrides]) => {
            const lookup = serviceLookup.get(serviceId)
            if (!lookup) {
                return
            }

            const changeList = []

            EDITABLE_FIELDS.forEach((field) => {
                if (!(field in overrides)) {
                    return
                }

                const originalValue = lookup.service?.[field]
                const updatedValue = overrides[field]
                const originalNormalized = normalizeComparisonValue(field, originalValue)
                const updatedNormalized = normalizeComparisonValue(field, updatedValue)

                if (originalNormalized === updatedNormalized) {
                    return
                }

                changeList.push({
                    field,
                    label: FIELD_LABELS[field] || field,
                    from: formatDisplayValue(field, originalValue),
                    to: formatDisplayValue(field, updatedValue),
                })
            })

            if (changeList.length === 0) {
                return
            }

            totalFieldChanges += changeList.length
            entries.push({
                serviceId,
                serviceName: lookup.service?.serviceName || 'Unnamed service',
                categoryName: lookup.category?.categoryName || 'Uncategorised',
                changes: changeList,
            })
        })

        entries.sort((a, b) => a.serviceName.localeCompare(b.serviceName, undefined, { sensitivity: 'accent' }))

        return {
            entries,
            totalServices: entries.length,
            totalFieldChanges,
        }
    }, [editedServices, serviceLookup])

    const hasPendingEdits = React.useMemo(() => Object.keys(editedServices).length > 0, [editedServices])
    const hasDetectableChanges = savePreviewDetails.totalServices > 0
    const isSaveDisabled = !hasPendingEdits || !hasDetectableChanges || isSubmittingChanges

    React.useEffect(() => {
        if (!isSavePreviewOpen) {
            return
        }

        if (!isSubmittingChanges && savePreviewDetails.totalServices === 0) {
            setSavePreviewOpen(false)
        }
    }, [isSavePreviewOpen, isSubmittingChanges, savePreviewDetails.totalServices])

    // Handle input change
    const handleInputChange = (serviceId, field, value) => {
        setEditedServices(prev => ({
            ...prev,
            [serviceId]: {
                ...(prev[serviceId] || {}),
                [field]: value
            }
        }))
    }

    // Handle checkbox selection
    const handleSelectService = (serviceId) => {
        setSelectedServices(prev => {
            const newSet = new Set(prev)
            if (newSet.has(serviceId)) {
                newSet.delete(serviceId)
            } else {
                newSet.add(serviceId)
            }
            return newSet
        })
    }

    // Handle select all
    const handleSelectAll = () => {
        if (selectedServices.size === totalServices) {
            setSelectedServices(new Set())
        } else {
            const allServiceIds = new Set()
            priceList.forEach(category => {
                category.services?.forEach(service => {
                    allServiceIds.add(service.identifier)
                })
            })
            setSelectedServices(allServiceIds)
        }
    }

    const getServiceFieldValue = React.useCallback((service, field) => {
        const overrides = editedServices[service.identifier]
        if (overrides && overrides[field] !== undefined && overrides[field] !== null) {
            return overrides[field]
        }
        const original = service[field]
        if (original === undefined || original === null) {
            return ''
        }
        return typeof original === 'number' ? original.toString() : String(original)
    }, [editedServices])

    const formatAdjustmentValue = React.useCallback((value, mode) => {
        const numeric = Number(value)
        if (!Number.isFinite(numeric)) {
            return mode === 'percentage' ? '0%' : '₹0'
        }
        const normalized = Number(numeric.toFixed(2))
        if (mode === 'percentage') {
            return `${normalized}%`
        }
        return `₹${normalized}`
    }, [])

    // Handle bulk actions
    const handleBulkUpdate = () => {
        if (isSaveDisabled) {
            if (hasPendingEdits && !hasDetectableChanges) {
                setSaveFeedback({
                    type: 'info',
                    text: 'All edits match the current pricelist. Update a field before saving.',
                })
            }
            return
        }

        setSavePreviewError('')
        setSaveFeedback(null)
        setSavePreviewOpen(true)
    }

    const handleToggleGroup = React.useCallback((groupId) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev)
            if (next.has(groupId)) {
                next.delete(groupId)
            } else {
                next.add(groupId)
            }
            return next
        })
    }, [])

    const handleCloseSavePreview = React.useCallback(() => {
        if (isSubmittingChanges) {
            return
        }
        setSavePreviewOpen(false)
        setSavePreviewError('')
    }, [isSubmittingChanges])

    const applyLocalBulkChanges = React.useCallback(() => {
        setPriceList(prev => prev.map(category => {
            if (!Array.isArray(category.services) || category.services.length === 0) {
                return category
            }

            let hasChanges = false
            const updatedServices = category.services.map(service => {
                const overrides = editedServices[service.identifier]
                if (!overrides) {
                    return service
                }

                let modified = false
                const updated = { ...service }

                EDITABLE_FIELDS.forEach((field) => {
                    if (field in overrides) {
                        const coerced = coerceFieldValueForPersist(field, overrides[field])
                        if (updated[field] !== coerced) {
                            updated[field] = coerced
                            modified = true
                        }
                    }
                })

                if (modified) {
                    hasChanges = true
                    return updated
                }

                return service
            })

            return hasChanges ? { ...category, services: updatedServices } : category
        }))
    }, [editedServices])

    const handleConfirmBulkUpdate = React.useCallback(async () => {
        if (isSubmittingChanges) {
            return
        }

        if (savePreviewDetails.totalServices === 0) {
            setSavePreviewOpen(false)
            return
        }

        setIsSubmittingChanges(true)
        setSavePreviewError('')

        const servicesChanged = savePreviewDetails.totalServices
        const fieldChanges = savePreviewDetails.totalFieldChanges

        const changesPayload = savePreviewDetails.entries.map(entry => {
            const overrides = editedServices[entry.serviceId] || {}
            const changeFields = {}

            entry.changes.forEach(change => {
                if (change.field in overrides) {
                    changeFields[change.field] = coerceFieldValueForPersist(change.field, overrides[change.field])
                }
            })

            const lookup = serviceLookup.get(entry.serviceId)
            const categoryIdentifier = lookup?.category?.categoryIdentifier

            return {
                serviceIdentifier: entry.serviceId,
                ...(categoryIdentifier ? { categoryIdentifier } : {}),
                updates: changeFields,
            }
        }).filter(item => Object.keys(item.updates).length > 0)

        if (changesPayload.length === 0) {
            setSavePreviewError('No changes detected to submit.')
            setIsSubmittingChanges(false)
            return
        }

        if (!authCtx?.token) {
            setSavePreviewError('Missing authentication token. Please log in again.')
            setIsSubmittingChanges(false)
            return
        }

        if (!activeStore?.storeIdentifier) {
            setSavePreviewError('Select a store before saving changes.')
            setIsSubmittingChanges(false)
            return
        }

        try {
            const response = await fetch(BASE_URL_API + "updatePriceListBulk", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    updates: changesPayload,
                }),
            })

            if (!response.ok) {
                throw new Error('Server error while updating pricelist.')
            }

            const respo = await response.json()

            if (respo?.status !== 'success') {
                if (respo?.message === 'tokenExpired') {
                    setSavePreviewError('Session expired. Please log in again.')
                    setSavePreviewOpen(false)
                    authCtx.logout()
                    return
                }
                throw new Error(respo?.message || 'Bulk update failed. Please try again.')
            }

            if (Array.isArray(respo?.response)) {
                setPriceList(respo.response)
            } else {
                applyLocalBulkChanges()
            }

            setEditedServices({})
            setSavePreviewOpen(false)
            setSaveFeedback({
                type: 'success',
                text: `Applied ${fieldChanges} change${fieldChanges === 1 ? '' : 's'} across ${servicesChanged} service${servicesChanged === 1 ? '' : 's'}.`,
            })
        } catch (error) {
            console.error('Error applying price list changes:', error)
            setSavePreviewError(error?.message || 'Could not apply changes. Please try again.')
        } finally {
            setIsSubmittingChanges(false)
        }
    }, [activeStore, applyLocalBulkChanges, authCtx, editedServices, isSubmittingChanges, savePreviewDetails, serviceLookup])

    const handleBulkAction = (actionId) => {
        const action = bulkActionDictionary[actionId]
        if (!action) {
            return
        }

        if (!isBulkActionsOpen) {
            setBulkActionsOpen(true)
        }

        setActiveActionId(actionId)
        setPriceAdjustmentError('')
        setRoundingError('')
        setDurationError('')
        setDurationIncreaseError('')
        setDiscountError('')
        setDiscountRemoveError('')
        setNameAffixError('')
        setNameStandardizeError('')
        setNameReplaceError('')
        setDuplicateError('')
        setHsnUpdateError('')
        setHsnClearError('')
        console.log('Bulk action triggered:', actionId, {
            selectedServices: Array.from(selectedServices),
            editedServices,
        })
    }

    const applyPriceAdjustment = React.useCallback((event) => {
        event.preventDefault()

        if (!isPriceAdjustmentAction) {
            return
        }

        if (selectedServices.size === 0) {
            setPriceAdjustmentError('Select at least one service to continue.')
            setPriceAdjustmentSummary(null)
            return
        }

        const numericValue = Number.parseFloat(priceAdjustmentValue)
        if (!Number.isFinite(numericValue) || numericValue <= 0) {
            setPriceAdjustmentError('Enter a value greater than 0.')
            setPriceAdjustmentSummary(null)
            return
        }

        const direction = activeActionId === 'price-increase' ? 'increase' : 'decrease'
        const nextEdited = { ...editedServices }
        const totalSelected = selectedServices.size
        let appliedCount = 0

        priceList.forEach(category => {
            category.services?.forEach(service => {
                if (!selectedServices.has(service.identifier)) {
                    return
                }

                const existingOverride = nextEdited[service.identifier]
                const sourceValue = existingOverride?.basePrice ?? service.basePrice
                const current = Number.parseFloat(sourceValue)

                if (!Number.isFinite(current)) {
                    return
                }

                let updated = current
                if (priceAdjustmentMode === 'percentage') {
                    const factor = numericValue / 100
                    updated = direction === 'increase'
                        ? current * (1 + factor)
                        : current * (1 - factor)
                } else {
                    updated = direction === 'increase'
                        ? current + numericValue
                        : current - numericValue
                }

                if (updated < 0) {
                    updated = 0
                }

                const rounded = Math.round(updated * 100) / 100
                const normalized = Number(rounded.toFixed(2))
                const priceString = Number.isFinite(normalized) ? normalized.toString() : current.toString()
                const originalPriceString = service.basePrice === undefined || service.basePrice === null
                    ? ''
                    : service.basePrice.toString()

                const updatedOverride = {
                    ...(existingOverride || {}),
                }

                if (priceString === originalPriceString) {
                    delete updatedOverride.basePrice
                } else {
                    updatedOverride.basePrice = priceString
                }

                const previousOverride = existingOverride || {}
                const previousKeys = Object.keys(previousOverride)
                const updatedKeys = Object.keys(updatedOverride)
                const keysChanged = previousKeys.length !== updatedKeys.length ||
                    previousKeys.some(key => previousOverride[key] !== updatedOverride[key])

                if (!keysChanged) {
                    return
                }

                if (updatedKeys.length === 0) {
                    delete nextEdited[service.identifier]
                } else {
                    nextEdited[service.identifier] = updatedOverride
                }

                appliedCount += 1
            })
        })

        if (appliedCount === 0) {
            setPriceAdjustmentError('No selected services have a base price that can be adjusted.')
            setPriceAdjustmentSummary(null)
            return
        }

        setEditedServices(nextEdited)
        setPriceAdjustmentError('')
        setPriceAdjustmentSummary({
            count: appliedCount,
            totalSelected,
            mode: priceAdjustmentMode,
            value: numericValue,
            direction,
        })
    }, [isPriceAdjustmentAction, selectedServices, priceAdjustmentValue, activeActionId, editedServices, priceList, priceAdjustmentMode])

    const applyPriceRounding = React.useCallback((event) => {
        event.preventDefault()

        if (!isRoundingAction) {
            return
        }

        if (selectedServices.size === 0) {
            setRoundingError('Select at least one service to continue.')
            setRoundingSummary(null)
            return
        }

        const numericStep = Number.parseFloat(roundingStep)
        if (!Number.isFinite(numericStep) || numericStep <= 0) {
            setRoundingError('Enter a rounding step greater than 0.')
            setRoundingSummary(null)
            return
        }

        const nextEdited = { ...editedServices }
        const totalSelected = selectedServices.size
        let appliedCount = 0

        priceList.forEach(category => {
            category.services?.forEach(service => {
                if (!selectedServices.has(service.identifier)) {
                    return
                }

                const existingOverride = nextEdited[service.identifier]
                const sourceValue = existingOverride?.basePrice ?? service.basePrice
                const current = Number.parseFloat(sourceValue)

                if (!Number.isFinite(current)) {
                    return
                }

                const ratio = current / numericStep
                let updated = current

                if (roundingMode === 'nearest') {
                    updated = Math.round(ratio) * numericStep
                } else if (roundingMode === 'up') {
                    updated = Math.ceil(ratio) * numericStep
                } else {
                    updated = Math.floor(ratio) * numericStep
                }

                if (!Number.isFinite(updated)) {
                    return
                }

                if (updated < 0) {
                    updated = 0
                }

                const rounded = Math.round(updated * 100) / 100
                const normalized = Number(rounded.toFixed(2))
                const priceString = Number.isFinite(normalized) ? normalized.toString() : current.toString()
                const originalPriceString = service.basePrice === undefined || service.basePrice === null
                    ? ''
                    : service.basePrice.toString()

                const updatedOverride = {
                    ...(existingOverride || {}),
                }

                if (priceString === originalPriceString) {
                    delete updatedOverride.basePrice
                } else {
                    updatedOverride.basePrice = priceString
                }

                const previousOverride = existingOverride || {}
                const previousKeys = Object.keys(previousOverride)
                const updatedKeys = Object.keys(updatedOverride)
                const keysChanged = previousKeys.length !== updatedKeys.length ||
                    previousKeys.some(key => previousOverride[key] !== updatedOverride[key])

                if (!keysChanged) {
                    return
                }

                if (updatedKeys.length === 0) {
                    delete nextEdited[service.identifier]
                } else {
                    nextEdited[service.identifier] = updatedOverride
                }

                appliedCount += 1
            })
        })

        if (appliedCount === 0) {
            setRoundingError('No selected services have a base price that can be rounded.')
            setRoundingSummary(null)
            return
        }

        setEditedServices(nextEdited)
        setRoundingError('')
        setRoundingSummary({
            count: appliedCount,
            totalSelected,
            step: numericStep,
            mode: roundingMode,
        })
    }, [isRoundingAction, selectedServices, roundingStep, roundingMode, editedServices, priceList])

    const applyDurationSet = React.useCallback((event) => {
        event.preventDefault()

        if (!isDurationSetAction) {
            return
        }

        if (selectedServices.size === 0) {
            setDurationError('Select at least one service to continue.')
            setDurationSummary(null)
            return
        }

        const parsedValue = Number.parseFloat(durationValue)
        if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
            setDurationError('Enter a duration greater than 0 minutes.')
            setDurationSummary(null)
            return
        }

        const normalizedValue = Math.max(1, Math.round(parsedValue))
        const normalizedString = normalizedValue.toString()
        const normalizeDuration = (rawValue) => {
            if (rawValue === undefined || rawValue === null) {
                return null
            }
            const trimmed = String(rawValue).trim()
            if (trimmed === '') {
                return null
            }
            const numeric = Number.parseFloat(trimmed)
            if (!Number.isFinite(numeric)) {
                return null
            }
            return Math.round(numeric)
        }

        const nextEdited = { ...editedServices }
        const totalSelected = selectedServices.size
        let appliedCount = 0

        priceList.forEach(category => {
            category.services?.forEach(service => {
                if (!selectedServices.has(service.identifier)) {
                    return
                }

                const existingOverride = nextEdited[service.identifier]
                const overrideNumeric = normalizeDuration(existingOverride?.serviceTime)
                const originalNumeric = normalizeDuration(service.serviceTime)
                const currentNumeric = overrideNumeric ?? originalNumeric

                if (currentNumeric !== null && currentNumeric === normalizedValue) {
                    return
                }

                const updatedOverride = {
                    ...(existingOverride || {}),
                }

                if (originalNumeric !== null && originalNumeric === normalizedValue) {
                    delete updatedOverride.serviceTime
                } else {
                    updatedOverride.serviceTime = normalizedString
                }

                const previousOverride = existingOverride || {}
                const previousKeys = Object.keys(previousOverride)
                const updatedKeys = Object.keys(updatedOverride)
                const keysChanged = previousKeys.length !== updatedKeys.length ||
                    previousKeys.some(key => previousOverride[key] !== updatedOverride[key])

                if (!keysChanged) {
                    return
                }

                if (updatedKeys.length === 0) {
                    delete nextEdited[service.identifier]
                } else {
                    nextEdited[service.identifier] = updatedOverride
                }

                appliedCount += 1
            })
        })

        if (appliedCount === 0) {
            setDurationError('Selected services already have this duration set.')
            setDurationSummary(null)
            return
        }

        setEditedServices(nextEdited)
        setDurationError('')
        setDurationSummary({
            count: appliedCount,
            totalSelected,
            value: normalizedValue,
        })
        setDurationValue(normalizedString)
    }, [isDurationSetAction, selectedServices, durationValue, editedServices, priceList])

    const applyDurationIncrease = React.useCallback((event) => {
        event.preventDefault()

        if (!isDurationIncreaseAction) {
            return
        }

        if (selectedServices.size === 0) {
            setDurationIncreaseError('Select at least one service to continue.')
            setDurationIncreaseSummary(null)
            return
        }

        const parsedValue = Number.parseFloat(durationIncreaseValue)
        if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
            setDurationIncreaseError('Enter minutes greater than 0 to add.')
            setDurationIncreaseSummary(null)
            return
        }

        const incrementValue = Math.round(parsedValue)
        const nextEdited = { ...editedServices }
        const totalSelected = selectedServices.size
        let appliedCount = 0
        const updates = []

        const normalizeDuration = (rawValue) => {
            if (rawValue === undefined || rawValue === null) {
                return null
            }
            const trimmed = String(rawValue).trim()
            if (trimmed === '') {
                return null
            }
            const numeric = Number.parseFloat(trimmed)
            if (!Number.isFinite(numeric)) {
                return null
            }
            return Math.round(numeric)
        }

        priceList.forEach(category => {
            category.services?.forEach(service => {
                if (!selectedServices.has(service.identifier)) {
                    return
                }

                const existingOverride = nextEdited[service.identifier]
                const overrideNumeric = normalizeDuration(existingOverride?.serviceTime)
                const originalNumeric = normalizeDuration(service.serviceTime)
                const currentNumeric = overrideNumeric ?? originalNumeric

                if (currentNumeric === null) {
                    return
                }

                const updatedValue = Math.max(0, currentNumeric + incrementValue)
                const updatedString = updatedValue.toString()

                const updatedOverride = {
                    ...(existingOverride || {}),
                }

                if (originalNumeric !== null && originalNumeric === updatedValue) {
                    delete updatedOverride.serviceTime
                } else {
                    updatedOverride.serviceTime = updatedString
                }

                const previousOverride = existingOverride || {}
                const previousKeys = Object.keys(previousOverride)
                const updatedKeys = Object.keys(updatedOverride)
                const keysChanged = previousKeys.length !== updatedKeys.length ||
                    previousKeys.some(key => previousOverride[key] !== updatedOverride[key])

                if (!keysChanged) {
                    return
                }

                if (updatedKeys.length === 0) {
                    delete nextEdited[service.identifier]
                } else {
                    nextEdited[service.identifier] = updatedOverride
                }

                appliedCount += 1
                updates.push({ identifier: service.identifier, from: currentNumeric, to: updatedValue })
            })
        })

        if (appliedCount === 0) {
            setDurationIncreaseError('Unable to adjust duration. Ensure services have an existing duration set.')
            setDurationIncreaseSummary(null)
            return
        }

        setEditedServices(nextEdited)
        setDurationIncreaseError('')
        setDurationIncreaseSummary({
            count: appliedCount,
            totalSelected,
            increment: incrementValue,
            updates,
        })
        setDurationIncreaseValue(incrementValue.toString())
    }, [isDurationIncreaseAction, selectedServices, durationIncreaseValue, editedServices, priceList])

    const applyDurationDecrease = React.useCallback((event) => {
        event.preventDefault()

        if (!isDurationDecreaseAction) {
            return
        }

        if (selectedServices.size === 0) {
            setDurationDecreaseError('Select at least one service to continue.')
            setDurationDecreaseSummary(null)
            return
        }

        const parsedValue = Number.parseFloat(durationDecreaseValue)
        if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
            setDurationDecreaseError('Enter minutes greater than 0 to subtract.')
            setDurationDecreaseSummary(null)
            return
        }

        const decrementValue = Math.round(parsedValue)
        const nextEdited = { ...editedServices }
        const totalSelected = selectedServices.size
        let appliedCount = 0
        const updates = []

        const normalizeDuration = (rawValue) => {
            if (rawValue === undefined || rawValue === null) {
                return null
            }
            const trimmed = String(rawValue).trim()
            if (trimmed === '') {
                return null
            }
            const numeric = Number.parseFloat(trimmed)
            if (!Number.isFinite(numeric)) {
                return null
            }
            return Math.round(numeric)
        }

        priceList.forEach(category => {
            category.services?.forEach(service => {
                if (!selectedServices.has(service.identifier)) {
                    return
                }

                const existingOverride = nextEdited[service.identifier]
                const overrideNumeric = normalizeDuration(existingOverride?.serviceTime)
                const originalNumeric = normalizeDuration(service.serviceTime)
                const currentNumeric = overrideNumeric ?? originalNumeric

                if (currentNumeric === null || currentNumeric <= 0) {
                    return
                }

                const updatedValue = Math.max(0, currentNumeric - decrementValue)
                if (updatedValue === currentNumeric) {
                    return
                }
                const updatedString = updatedValue.toString()

                const updatedOverride = {
                    ...(existingOverride || {}),
                }

                if (originalNumeric !== null && originalNumeric === updatedValue) {
                    delete updatedOverride.serviceTime
                } else {
                    updatedOverride.serviceTime = updatedString
                }

                const previousOverride = existingOverride || {}
                const previousKeys = Object.keys(previousOverride)
                const updatedKeys = Object.keys(updatedOverride)
                const keysChanged = previousKeys.length !== updatedKeys.length ||
                    previousKeys.some(key => previousOverride[key] !== updatedOverride[key])

                if (!keysChanged) {
                    return
                }

                if (updatedKeys.length === 0) {
                    delete nextEdited[service.identifier]
                } else {
                    nextEdited[service.identifier] = updatedOverride
                }

                appliedCount += 1
                updates.push({ identifier: service.identifier, from: currentNumeric, to: updatedValue })
            })
        })

        if (appliedCount === 0) {
            setDurationDecreaseError('Unable to adjust duration. Ensure services have an existing duration above 0.')
            setDurationDecreaseSummary(null)
            return
        }

        setEditedServices(nextEdited)
        setDurationDecreaseError('')
        setDurationDecreaseSummary({
            count: appliedCount,
            totalSelected,
            decrement: decrementValue,
            updates,
        })
        setDurationDecreaseValue(decrementValue.toString())
    }, [isDurationDecreaseAction, selectedServices, durationDecreaseValue, editedServices, priceList])

    const applyNameStandardization = React.useCallback((event) => {
        event.preventDefault()

        if (!isNameStandardizeAction) {
            return
        }

        if (selectedServices.size === 0) {
            setNameStandardizeError('Select at least one service to continue.')
            setNameStandardizeSummary(null)
            return
        }

        const hasFormattingRule = nameStandardizeCase !== 'preserve' || nameStandardizeTrimEdges || nameStandardizeCollapseSpaces
        if (!hasFormattingRule) {
            setNameStandardizeError('Select at least one formatting rule before applying.')
            setNameStandardizeSummary(null)
            return
        }

        const nextEdited = { ...editedServices }
        const totalSelected = selectedServices.size
        let appliedCount = 0

        const normalizeValue = (value) => {
            if (value === undefined || value === null) {
                return ''
            }
            return String(value)
        }

        const standardizeValue = (rawValue) => {
            let working = normalizeValue(rawValue)
            if (!working) {
                return working
            }

            if (nameStandardizeCollapseSpaces) {
                working = working.replace(/\s+/g, ' ')
            }
            if (nameStandardizeTrimEdges) {
                working = working.trim()
            }

            switch (nameStandardizeCase) {
                case 'title': {
                    working = working
                        .split(' ')
                        .map(word => {
                            if (!word) {
                                return word
                            }
                            const lower = word.toLowerCase()
                            return lower.charAt(0).toUpperCase() + lower.slice(1)
                        })
                        .join(' ')
                    break
                }
                case 'sentence': {
                    const lowered = working.toLowerCase()
                    working = lowered.charAt(0).toUpperCase() + lowered.slice(1)
                    break
                }
                case 'upper':
                    working = working.toUpperCase()
                    break
                case 'lower':
                    working = working.toLowerCase()
                    break
                default:
                    break
            }

            if (nameStandardizeCollapseSpaces) {
                working = working.replace(/\s+/g, ' ')
            }
            if (nameStandardizeTrimEdges) {
                working = working.trim()
            }

            return working
        }

        priceList.forEach(category => {
            category.services?.forEach(service => {
                if (!selectedServices.has(service.identifier)) {
                    return
                }

                const existingOverride = nextEdited[service.identifier]
                const overrideHasValue = existingOverride && Object.prototype.hasOwnProperty.call(existingOverride, 'serviceName')
                const originalValue = normalizeValue(service.serviceName)
                const baseline = overrideHasValue
                    ? normalizeValue(existingOverride.serviceName)
                    : originalValue

                if (!baseline) {
                    return
                }

                const standardized = standardizeValue(baseline)

                if (!standardized) {
                    if (overrideHasValue && (!originalValue || standardized === originalValue)) {
                        const updatedOverride = {
                            ...(existingOverride || {}),
                        }
                        delete updatedOverride.serviceName

                        const previousOverride = existingOverride || {}
                        const previousKeys = Object.keys(previousOverride)
                        const updatedKeys = Object.keys(updatedOverride)
                        const keysChanged = previousKeys.length !== updatedKeys.length ||
                            previousKeys.some(key => previousOverride[key] !== updatedOverride[key])

                        if (!keysChanged) {
                            return
                        }

                        if (updatedKeys.length === 0) {
                            delete nextEdited[service.identifier]
                        } else {
                            nextEdited[service.identifier] = updatedOverride
                        }

                        appliedCount += 1
                    }
                    return
                }

                if (standardized === baseline) {
                    if (overrideHasValue && standardized === originalValue) {
                        const updatedOverride = {
                            ...(existingOverride || {}),
                        }
                        delete updatedOverride.serviceName

                        const previousOverride = existingOverride || {}
                        const previousKeys = Object.keys(previousOverride)
                        const updatedKeys = Object.keys(updatedOverride)
                        const keysChanged = previousKeys.length !== updatedKeys.length ||
                            previousKeys.some(key => previousOverride[key] !== updatedOverride[key])

                        if (!keysChanged) {
                            return
                        }

                        if (updatedKeys.length === 0) {
                            delete nextEdited[service.identifier]
                        } else {
                            nextEdited[service.identifier] = updatedOverride
                        }

                        appliedCount += 1
                    }
                    return
                }

                const updatedOverride = {
                    ...(existingOverride || {}),
                }

                if (standardized === originalValue) {
                    delete updatedOverride.serviceName
                } else {
                    updatedOverride.serviceName = standardized
                }

                const previousOverride = existingOverride || {}
                const previousKeys = Object.keys(previousOverride)
                const updatedKeys = Object.keys(updatedOverride)
                const keysChanged = previousKeys.length !== updatedKeys.length ||
                    previousKeys.some(key => previousOverride[key] !== updatedOverride[key])

                if (!keysChanged) {
                    return
                }

                if (updatedKeys.length === 0) {
                    delete nextEdited[service.identifier]
                } else {
                    nextEdited[service.identifier] = updatedOverride
                }

                appliedCount += 1
            })
        })

        if (appliedCount === 0) {
            setNameStandardizeError('No changes were required. Try enabling additional formatting options.')
            setNameStandardizeSummary(null)
            return
        }

        setEditedServices(nextEdited)
        setNameStandardizeError('')
        setNameStandardizeSummary({
            count: appliedCount,
            totalSelected,
            caseMode: NAME_CASE_LABELS[nameStandardizeCase] || 'Current casing',
            trimmed: nameStandardizeTrimEdges,
            collapsed: nameStandardizeCollapseSpaces,
        })
    }, [
        isNameStandardizeAction,
        selectedServices,
        nameStandardizeCase,
        nameStandardizeTrimEdges,
        nameStandardizeCollapseSpaces,
        editedServices,
        priceList,
    ])

    const applyNameAffix = React.useCallback((event) => {
        event.preventDefault()

        if (!isNameAffixAction) {
            return
        }

        if (selectedServices.size === 0) {
            setNameAffixError('Select at least one service to continue.')
            setNameAffixSummary(null)
            return
        }

        const hasPrefix = nameAffixPrefix.trim().length > 0
        const hasSuffix = nameAffixSuffix.trim().length > 0

        if (!hasPrefix && !hasSuffix) {
            setNameAffixError('Add a prefix and/or suffix before applying.')
            setNameAffixSummary(null)
            return
        }

        const prefix = hasPrefix ? nameAffixPrefix : ''
        const suffix = hasSuffix ? nameAffixSuffix : ''
        const nextEdited = { ...editedServices }
        const totalSelected = selectedServices.size
        let appliedCount = 0
        const updates = []

        const normalizeValue = (value) => {
            if (value === undefined || value === null) {
                return ''
            }
            return String(value)
        }

        priceList.forEach(category => {
            category.services?.forEach(service => {
                if (!selectedServices.has(service.identifier)) {
                    return
                }

                const existingOverride = nextEdited[service.identifier]
                const overrideHasValue = existingOverride && Object.prototype.hasOwnProperty.call(existingOverride, 'serviceName')
                const originalValue = normalizeValue(service.serviceName)
                const baseline = overrideHasValue
                    ? normalizeValue(existingOverride.serviceName)
                    : originalValue

                if (!baseline) {
                    return
                }

                let updatedName = baseline
                let changed = false

                if (prefix && !updatedName.startsWith(prefix)) {
                    updatedName = prefix + updatedName
                    changed = true
                }

                if (suffix && !updatedName.endsWith(suffix)) {
                    updatedName = updatedName + suffix
                    changed = true
                }

                if (!changed) {
                    return
                }

                const updatedOverride = {
                    ...(existingOverride || {}),
                }

                if (updatedName === originalValue) {
                    delete updatedOverride.serviceName
                } else {
                    updatedOverride.serviceName = updatedName
                }

                const previousOverride = existingOverride || {}
                const previousKeys = Object.keys(previousOverride)
                const updatedKeys = Object.keys(updatedOverride)
                const keysChanged = previousKeys.length !== updatedKeys.length ||
                    previousKeys.some(key => previousOverride[key] !== updatedOverride[key])

                if (!keysChanged) {
                    return
                }

                if (updatedKeys.length === 0) {
                    delete nextEdited[service.identifier]
                } else {
                    nextEdited[service.identifier] = updatedOverride
                }

                appliedCount += 1
                updates.push({
                    identifier: service.identifier,
                    from: baseline,
                    to: updatedName,
                })
            })
        })

        if (appliedCount === 0) {
            setNameAffixError('No changes were required. Try a different prefix or suffix.')
            setNameAffixSummary(null)
            return
        }

        setEditedServices(nextEdited)
        setNameAffixError('')
        setNameAffixSummary({
            count: appliedCount,
            totalSelected,
            prefixApplied: Boolean(prefix),
            suffixApplied: Boolean(suffix),
            examples: updates.slice(0, 3),
        })
    }, [
        isNameAffixAction,
        selectedServices,
        nameAffixPrefix,
        nameAffixSuffix,
        editedServices,
        priceList,
    ])

    const applyNameReplace = React.useCallback((event) => {
        event.preventDefault()

        if (!isNameReplaceAction) {
            return
        }

        if (selectedServices.size === 0) {
            setNameReplaceError('Select at least one service to continue.')
            setNameReplaceSummary(null)
            return
        }

        const findValue = nameReplaceFindValue
        if (!findValue || findValue.trim() === '') {
            setNameReplaceError('Enter the text you want to find.')
            setNameReplaceSummary(null)
            return
        }

        const replaceValue = nameReplaceReplaceValue ?? ''
        const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const patternSource = escapeRegExp(findValue)
        const patternFlags = nameReplaceCaseSensitive ? 'g' : 'gi'

        const nextEdited = { ...editedServices }
        const totalSelected = selectedServices.size
        let appliedCount = 0
        const updates = []

        const normalizeValue = (value) => {
            if (value === undefined || value === null) {
                return ''
            }
            return String(value)
        }

        priceList.forEach(category => {
            category.services?.forEach(service => {
                if (!selectedServices.has(service.identifier)) {
                    return
                }

                const existingOverride = nextEdited[service.identifier]
                const overrideHasValue = existingOverride && Object.prototype.hasOwnProperty.call(existingOverride, 'serviceName')
                const originalValue = normalizeValue(service.serviceName)
                const baseline = overrideHasValue
                    ? normalizeValue(existingOverride.serviceName)
                    : originalValue

                if (!baseline) {
                    return
                }

                const pattern = new RegExp(patternSource, patternFlags)
                if (!pattern.test(baseline)) {
                    return
                }

                const updatedName = baseline.replace(new RegExp(patternSource, patternFlags), replaceValue)

                if (updatedName === baseline) {
                    return
                }

                const updatedOverride = {
                    ...(existingOverride || {}),
                }

                if (updatedName === originalValue) {
                    delete updatedOverride.serviceName
                } else {
                    updatedOverride.serviceName = updatedName
                }

                const previousOverride = existingOverride || {}
                const previousKeys = Object.keys(previousOverride)
                const updatedKeys = Object.keys(updatedOverride)
                const keysChanged = previousKeys.length !== updatedKeys.length ||
                    previousKeys.some(key => previousOverride[key] !== updatedOverride[key])

                if (!keysChanged) {
                    return
                }

                if (updatedKeys.length === 0) {
                    delete nextEdited[service.identifier]
                } else {
                    nextEdited[service.identifier] = updatedOverride
                }

                appliedCount += 1
                updates.push({
                    identifier: service.identifier,
                    from: baseline,
                    to: updatedName,
                })
            })
        })

        if (appliedCount === 0) {
            setNameReplaceError('No matches were found. Try adjusting your search text or case sensitivity.')
            setNameReplaceSummary(null)
            return
        }

        setEditedServices(nextEdited)
        setNameReplaceError('')
        setNameReplaceSummary({
            count: appliedCount,
            totalSelected,
            findValue,
            replaceValue,
            caseSensitive: nameReplaceCaseSensitive,
            examples: updates.slice(0, 3),
        })
    }, [
        isNameReplaceAction,
        selectedServices,
        nameReplaceFindValue,
        nameReplaceReplaceValue,
        nameReplaceCaseSensitive,
        editedServices,
        priceList,
    ])

    const analyzeDuplicateServiceNames = React.useCallback((event) => {
        event.preventDefault()

        if (!isDuplicateAnalysisAction) {
            return
        }

        if (duplicateSelectionOnly && selectedServices.size === 0) {
            setDuplicateError('Select one or more services or disable the "Only check selected" option.')
            setDuplicateSummary(null)
            return
        }

        const normalizeName = (value) => {
            if (value === undefined || value === null) {
                return ''
            }
            let working = String(value)
            if (duplicateTrimWhitespace) {
                working = working.replace(/\s+/g, ' ').trim()
            }
            if (!duplicateCaseSensitive) {
                working = working.toLowerCase()
            }
            return working
        }

        const buckets = new Map()
        let totalConsidered = 0
        const scopeSelected = duplicateSelectionOnly ? selectedServices : null

        priceList.forEach(category => {
            category.services?.forEach(service => {
                if (scopeSelected && !scopeSelected.has(service.identifier)) {
                    return
                }

                const rawName = service.serviceName
                const normalized = normalizeName(rawName)

                if (!normalized) {
                    return
                }

                totalConsidered += 1

                if (!buckets.has(normalized)) {
                    buckets.set(normalized, {
                        normalized,
                        services: [],
                    })
                }

                const bucket = buckets.get(normalized)
                bucket.services.push({
                    identifier: service.identifier,
                    name: rawName || '',
                    categoryName: category.categoryName || '',
                })
            })
        })

        const duplicates = []
        buckets.forEach(bucket => {
            if (bucket.services.length > 1) {
                const displayName = duplicateTrimWhitespace
                    ? (bucket.services[0]?.name || '').replace(/\s+/g, ' ').trim()
                    : (bucket.services[0]?.name || '')
                duplicates.push({
                    displayName,
                    count: bucket.services.length,
                    services: bucket.services,
                })
            }
        })

        if (duplicates.length === 0) {
            setDuplicateError('No duplicate service names found with the current options.')
            setDuplicateSummary(null)
            return
        }

        duplicates.sort((a, b) => {
            if (b.count !== a.count) {
                return b.count - a.count
            }
            return a.displayName.localeCompare(b.displayName)
        })

        const duplicateServiceCount = duplicates.reduce((acc, entry) => acc + entry.count, 0)

        setDuplicateError('')
        setDuplicateSummary({
            duplicates,
            duplicateNameCount: duplicates.length,
            duplicateServiceCount,
            totalConsidered,
            caseSensitive: duplicateCaseSensitive,
            trimWhitespace: duplicateTrimWhitespace,
            selectionOnly: duplicateSelectionOnly,
        })
    }, [
        isDuplicateAnalysisAction,
        duplicateSelectionOnly,
        selectedServices,
        duplicateTrimWhitespace,
        duplicateCaseSensitive,
        priceList,
    ])

    const applyDiscount = React.useCallback((event) => {
        event.preventDefault()

        if (!isDiscountApplyAction) {
            return
        }

        if (selectedServices.size === 0) {
            setDiscountError('Select at least one service to continue.')
            setDiscountSummary(null)
            return
        }

        const parsedValue = Number.parseFloat(discountValue)
        if (!Number.isFinite(parsedValue) || parsedValue < 0 || parsedValue > 100) {
            setDiscountError('Enter a discount between 0 and 100%.')
            setDiscountSummary(null)
            return
        }

        const normalizedValue = Math.max(0, Math.min(100, Math.round(parsedValue * 100) / 100))
        const normalizedString = normalizedValue.toString()
        const normalizePercentage = (rawValue) => {
            if (!rawValue) {
                return null
            }
            const parsed = Number.parseFloat(rawValue)
            if (!Number.isFinite(parsed)) {
                return null
            }
            return Math.round(parsed * 100) / 100
        }
        const nextEdited = { ...editedServices }
        const totalSelected = selectedServices.size
        let appliedCount = 0

        priceList.forEach(category => {
            category.services?.forEach(service => {
                if (!selectedServices.has(service.identifier)) {
                    return
                }

                const existingOverride = nextEdited[service.identifier]
                const overrideValue = existingOverride?.offerPercentage === undefined || existingOverride?.offerPercentage === null
                    ? ''
                    : String(existingOverride.offerPercentage).trim()
                const originalValue = service.offerPercentage === undefined || service.offerPercentage === null
                    ? ''
                    : String(service.offerPercentage).trim()
                const overrideNumeric = normalizePercentage(overrideValue)
                const originalNumeric = normalizePercentage(originalValue)
                const currentNumeric = overrideNumeric ?? originalNumeric

                if (currentNumeric !== null && Math.abs(currentNumeric - normalizedValue) < 0.0001) {
                    return
                }

                const updatedOverride = {
                    ...(existingOverride || {}),
                }

                if (originalNumeric !== null && Math.abs(originalNumeric - normalizedValue) < 0.0001) {
                    delete updatedOverride.offerPercentage
                } else {
                    updatedOverride.offerPercentage = normalizedString
                }

                const previousOverride = existingOverride || {}
                const previousKeys = Object.keys(previousOverride)
                const updatedKeys = Object.keys(updatedOverride)
                const keysChanged = previousKeys.length !== updatedKeys.length ||
                    previousKeys.some(key => previousOverride[key] !== updatedOverride[key])

                if (!keysChanged) {
                    return
                }

                if (updatedKeys.length === 0) {
                    delete nextEdited[service.identifier]
                } else {
                    nextEdited[service.identifier] = updatedOverride
                }

                appliedCount += 1
            })
        })

        if (appliedCount === 0) {
            setDiscountError('Selected services already use this discount value.')
            setDiscountSummary(null)
            return
        }

        setEditedServices(nextEdited)
        setDiscountError('')
        setDiscountSummary({
            count: appliedCount,
            totalSelected,
            value: normalizedValue,
        })
        setDiscountValue(normalizedString)
    }, [isDiscountApplyAction, selectedServices, discountValue, editedServices, priceList])

    const applyDiscountRemoval = React.useCallback((event) => {
        event.preventDefault()

        if (!isDiscountRemoveAction) {
            return
        }

        if (selectedServices.size === 0) {
            setDiscountRemoveError('Select at least one service to continue.')
            setDiscountRemoveSummary(null)
            return
        }

        const stringify = (value) => {
            if (value === undefined || value === null) {
                return ''
            }
            return String(value).trim()
        }

        const nextEdited = { ...editedServices }
        const totalSelected = selectedServices.size
        let appliedCount = 0

        priceList.forEach(category => {
            category.services?.forEach(service => {
                if (!selectedServices.has(service.identifier)) {
                    return
                }

                const existingOverride = nextEdited[service.identifier]
                const overrideValue = stringify(existingOverride?.offerPercentage)
                const originalValue = stringify(service.offerPercentage)

                if (!overrideValue && !originalValue) {
                    return
                }

                const updatedOverride = {
                    ...(existingOverride || {}),
                }

                updatedOverride.offerPercentage = ''

                if (!originalValue) {
                    delete updatedOverride.offerPercentage
                }

                const previousOverride = existingOverride || {}
                const previousKeys = Object.keys(previousOverride)
                const updatedKeys = Object.keys(updatedOverride)
                const keysChanged = previousKeys.length !== updatedKeys.length ||
                    previousKeys.some(key => previousOverride[key] !== updatedOverride[key])

                if (!keysChanged) {
                    return
                }

                if (updatedKeys.length === 0) {
                    delete nextEdited[service.identifier]
                } else {
                    nextEdited[service.identifier] = updatedOverride
                }

                appliedCount += 1
            })
        })

        if (appliedCount === 0) {
            setDiscountRemoveError('Selected services already have no discount applied.')
            setDiscountRemoveSummary(null)
            return
        }

        setEditedServices(nextEdited)
        setDiscountRemoveError('')
        setDiscountRemoveSummary({
            count: appliedCount,
            totalSelected,
        })
    }, [isDiscountRemoveAction, selectedServices, editedServices, priceList])

    const applyHsnCodeUpdate = React.useCallback((event) => {
        event.preventDefault()

        if (!isHsnBulkUpdateAction) {
            return
        }

        if (selectedServices.size === 0) {
            setHsnUpdateError('Select at least one service to continue.')
            setHsnUpdateSummary(null)
            return
        }

        const normalizedCode = selectedHsnCode.trim()
        if (!normalizedCode) {
            setHsnUpdateError('Choose an HSN/SAC code to apply.')
            setHsnUpdateSummary(null)
            return
        }

        const nextEdited = { ...editedServices }
        const totalSelected = selectedServices.size
        let appliedCount = 0

        priceList.forEach(category => {
            category.services?.forEach(service => {
                if (!selectedServices.has(service.identifier)) {
                    return
                }

                const existingOverride = nextEdited[service.identifier]
                const overrideValue = existingOverride?.hsnCode === undefined || existingOverride?.hsnCode === null
                    ? ''
                    : String(existingOverride.hsnCode).trim()
                const originalValue = service.hsnCode === undefined || service.hsnCode === null
                    ? ''
                    : String(service.hsnCode).trim()
                const currentValue = overrideValue || originalValue

                if (currentValue === normalizedCode) {
                    return
                }

                const updatedOverride = {
                    ...(existingOverride || {}),
                }

                if (normalizedCode === originalValue) {
                    delete updatedOverride.hsnCode
                } else {
                    updatedOverride.hsnCode = normalizedCode
                }

                const previousOverride = existingOverride || {}
                const previousKeys = Object.keys(previousOverride)
                const updatedKeys = Object.keys(updatedOverride)
                const keysChanged = previousKeys.length !== updatedKeys.length ||
                    previousKeys.some(key => previousOverride[key] !== updatedOverride[key])

                if (!keysChanged) {
                    return
                }

                if (updatedKeys.length === 0) {
                    delete nextEdited[service.identifier]
                } else {
                    nextEdited[service.identifier] = updatedOverride
                }

                appliedCount += 1
            })
        })

        if (appliedCount === 0) {
            setHsnUpdateError('Selected services already use this HSN code.')
            setHsnUpdateSummary(null)
            return
        }

        setEditedServices(nextEdited)
        setHsnUpdateError('')
        setHsnUpdateSummary({
            count: appliedCount,
            totalSelected,
            code: normalizedCode,
        })
    }, [isHsnBulkUpdateAction, selectedServices, selectedHsnCode, editedServices, priceList])

    const applyHsnCodeClear = React.useCallback((event) => {
        event.preventDefault()

        if (!isHsnClearAction) {
            return
        }

        if (selectedServices.size === 0) {
            setHsnClearError('Select at least one service to continue.')
            setHsnClearSummary(null)
            return
        }

        const nextEdited = { ...editedServices }
        const totalSelected = selectedServices.size
        let appliedCount = 0

        priceList.forEach(category => {
            category.services?.forEach(service => {
                if (!selectedServices.has(service.identifier)) {
                    return
                }

                const existingOverride = nextEdited[service.identifier]
                const overrideValue = existingOverride?.hsnCode === undefined || existingOverride?.hsnCode === null
                    ? ''
                    : String(existingOverride.hsnCode).trim()
                const originalValue = service.hsnCode === undefined || service.hsnCode === null
                    ? ''
                    : String(service.hsnCode).trim()

                if (!overrideValue && !originalValue) {
                    return
                }

                const updatedOverride = {
                    ...(existingOverride || {}),
                }

                updatedOverride.hsnCode = ''

                const previousOverride = existingOverride || {}
                const previousKeys = Object.keys(previousOverride)
                const updatedKeys = Object.keys(updatedOverride)
                const keysChanged = previousKeys.length !== updatedKeys.length ||
                    previousKeys.some(key => previousOverride[key] !== updatedOverride[key])

                if (!keysChanged) {
                    return
                }

                nextEdited[service.identifier] = updatedOverride
                appliedCount += 1
            })
        })

        if (appliedCount === 0) {
            setHsnClearError('Selected services already have empty HSN codes.')
            setHsnClearSummary(null)
            return
        }

        setEditedServices(nextEdited)
        setHsnClearError('')
        setHsnClearSummary({
            count: appliedCount,
            totalSelected,
        })
    }, [isHsnClearAction, selectedServices, editedServices, priceList])

    const handleExport = () => {
        console.log("Export pricelist")
        // Implement export logic
    }

    const containerClassName = [
        styles.container,
        isBulkActionsOpen
            ? (activeActionId ? styles.containerWithPanelExpanded : styles.containerWithPanel)
            : '',
    ].filter(Boolean).join(' ')

    const panelClassName = [
        styles.bulkActionsPanel,
        isBulkActionsOpen ? styles.bulkActionsPanelOpen : '',
        activeActionId ? styles.bulkActionsPanelExpanded : '',
    ].filter(Boolean).join(' ')

    const priceAdjustmentNumericValue = Number.parseFloat(priceAdjustmentValue)
    const isPriceAdjustmentValueValid = Number.isFinite(priceAdjustmentNumericValue) && priceAdjustmentNumericValue > 0
    const roundingStepNumericValue = Number.parseFloat(roundingStep)
    const isRoundingStepValid = Number.isFinite(roundingStepNumericValue) && roundingStepNumericValue > 0
    const durationNumericValue = Number.parseFloat(durationValue)
    const isDurationValueValid = Number.isFinite(durationNumericValue) && durationNumericValue > 0
    const durationIncreaseNumericValue = Number.parseFloat(durationIncreaseValue)
    const isDurationIncreaseValueValid = Number.isFinite(durationIncreaseNumericValue) && durationIncreaseNumericValue > 0
    const durationDecreaseNumericValue = Number.parseFloat(durationDecreaseValue)
    const isDurationDecreaseValueValid = Number.isFinite(durationDecreaseNumericValue) && durationDecreaseNumericValue > 0
    const isNameStandardizeConfigured = nameStandardizeCase !== 'preserve' || nameStandardizeTrimEdges || nameStandardizeCollapseSpaces
    const isNameAffixConfigured = nameAffixPrefix.trim().length > 0 || nameAffixSuffix.trim().length > 0
    const isNameReplaceConfigured = nameReplaceFindValue.trim().length > 0
    const discountNumericValue = Number.parseFloat(discountValue)
    const isDiscountValueValid = Number.isFinite(discountNumericValue) && discountNumericValue >= 0 && discountNumericValue <= 100
    const hsnSelectOptions = React.useMemo(() => (
        hsnCodes.map(entry => ({
            value: entry.code,
            label: entry.description
                ? `${entry.code} · ${entry.description}`
                : entry.code,
        }))
    ), [hsnCodes])
    const selectedHsnOption = React.useMemo(() => (
        selectedHsnCode ? hsnSelectOptions.find(option => option.value === selectedHsnCode) || null : null
    ), [hsnSelectOptions, selectedHsnCode])
    const selectedHsnCodeDetail = React.useMemo(() => (
        selectedHsnCode ? hsnCodes.find(entry => entry.code === selectedHsnCode) || null : null
    ), [hsnCodes, selectedHsnCode])
    const isHsnSelectionValid = Boolean(selectedHsnCode.trim())

    const getHsnSacCodes = React.useCallback(async () => {
        setIsLoadingHsnCodes(true)
        try {
            const response = await fetch(BASE_URL_API + "getHsnSacCodes", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                })
            })

            if (!response.ok) {
                console.log("Something went wrong : Server Error")
                setHsnCodes([])
                return
            }

            const respo = await response.json()
            if (respo?.status === "success") {
                const candidates = [
                    respo?.response,
                    respo?.response?.hsnSacCodes,
                    respo?.response?.hsnCodes,
                    respo?.response?.data,
                ]
                const rawList = candidates.find(Array.isArray) || []

                const normalized = rawList.map((item, index) => {
                    if (item === null || item === undefined) {
                        return null
                    }

                    if (typeof item === 'string' || typeof item === 'number') {
                        const codeString = String(item).trim()
                        if (!codeString) {
                            return null
                        }
                        return {
                            code: codeString,
                            description: '',
                            __index: index,
                        }
                    }

                    if (typeof item === 'object') {
                        let code = ''
                        for (const key of HSN_CODE_KEYS) {
                            if (item[key] !== undefined && item[key] !== null) {
                                const value = String(item[key]).trim()
                                if (value) {
                                    code = value
                                    break
                                }
                            }
                        }

                        if (!code) {
                            return null
                        }

                        let description = ''
                        for (const key of HSN_DESCRIPTION_KEYS) {
                            if (item[key] !== undefined && item[key] !== null) {
                                const value = String(item[key]).trim()
                                if (value && value.toLowerCase() !== code.toLowerCase()) {
                                    description = value
                                    break
                                }
                            }
                        }

                        return {
                            code,
                            description,
                            __index: index,
                        }
                    }

                    return null
                }).filter(Boolean)

                const uniqueMap = new Map()
                normalized.forEach(entry => {
                    if (!uniqueMap.has(entry.code)) {
                        uniqueMap.set(entry.code, {
                            code: entry.code,
                            description: entry.description,
                        })
                    }
                })

                setHsnCodes(Array.from(uniqueMap.values()))
            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                } else {
                    console.log(respo)
                }
                setHsnCodes([])
            }
        } catch (error) {
            console.error("Error fetching HSN/SAC codes:", error)
            setHsnCodes([])
        } finally {
            setIsLoadingHsnCodes(false)
        }
    }, [authCtx])

    React.useEffect(() => {
        getHsnSacCodes()
    }, [getHsnSacCodes])


    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.loading}>
                    <div className={styles.spinner}></div>
                    <p>Loading pricelist...</p>
                </div>
            </div>
        )
    }

    if (totalServices === 0) {
        return (
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.headerTop}>
                        <h1 className={styles.title}>Pricelist</h1>
                    </div>
                </div>
                <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>📋</div>
                    <h2 className={styles.emptyTitle}>No services found</h2>
                    <p className={styles.emptyText}>Add services to get started with your pricelist</p>
                </div>
            </div>
        )
    }

    return (
        <div className={containerClassName}>
            {isSavePreviewOpen && (
                <div
                    className={styles.savePreviewOverlay}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="save-preview-title"
                    onClick={(event) => {
                        if (event.target === event.currentTarget) {
                            handleCloseSavePreview()
                        }
                    }}
                >
                    <div
                        className={styles.savePreviewDialog}
                        role="document"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <header className={styles.savePreviewHeader}>
                            <div className={styles.savePreviewHeaderText}>
                                <h3 id="save-preview-title" className={styles.savePreviewTitle}>Review &amp; Confirm</h3>
                                <p className={styles.savePreviewMeta}>
                                    {`${savePreviewDetails.totalFieldChanges} change${savePreviewDetails.totalFieldChanges === 1 ? '' : 's'} across ${savePreviewDetails.totalServices} service${savePreviewDetails.totalServices === 1 ? '' : 's'}.`}
                                </p>
                            </div>
                            <button
                                type="button"
                                className={styles.savePreviewClose}
                                onClick={handleCloseSavePreview}
                                aria-label="Close review dialog"
                                disabled={isSubmittingChanges}
                            >
                                <X size={16} strokeWidth={1.8} />
                            </button>
                        </header>
                        <div className={styles.savePreviewBody}>
                            <p className={styles.savePreviewIntro}>
                                Preview the pending edits before submitting them to the pricelist.
                            </p>
                            {savePreviewError && (
                                <p className={styles.errorMessage} role="alert">{savePreviewError}</p>
                            )}
                            {savePreviewDetails.entries.length === 0 ? (
                                <p className={styles.savePreviewEmpty}>No changes detected.</p>
                            ) : (
                                <div className={styles.savePreviewList}>
                                    {savePreviewDetails.entries.map(entry => (
                                        <article key={entry.serviceId} className={styles.savePreviewItem}>
                                            <div className={styles.savePreviewItemHeader}>
                                                <span className={styles.savePreviewItemTitle}>{entry.serviceName}</span>
                                                <span className={styles.savePreviewItemMeta}>{entry.categoryName}</span>
                                            </div>
                                            <div className={styles.savePreviewChanges}>
                                                {entry.changes.map((change) => (
                                                    <div key={`${entry.serviceId}-${change.field}`} className={styles.savePreviewFieldChange}>
                                                        <span className={styles.savePreviewFieldLabel}>{change.label}</span>
                                                        <div className={styles.savePreviewDiff}>
                                                            <span className={styles.savePreviewDiffFrom}>{change.from}</span>
                                                            <span className={styles.savePreviewDiffArrow}>→</span>
                                                            <span className={styles.savePreviewDiffTo}>{change.to}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </article>
                                    ))}
                                </div>
                            )}
                        </div>
                        <footer className={styles.savePreviewFooter}>
                            <button
                                type="button"
                                className={styles.savePreviewSecondary}
                                onClick={handleCloseSavePreview}
                                disabled={isSubmittingChanges}
                            >
                                Keep Editing
                            </button>
                            <button
                                type="button"
                                className={styles.savePreviewPrimary}
                                onClick={handleConfirmBulkUpdate}
                                disabled={isSubmittingChanges}
                                aria-busy={isSubmittingChanges}
                            >
                                {isSubmittingChanges ? 'Submitting...' : 'Submit Changes'}
                            </button>
                        </footer>
                    </div>
                </div>
            )}

            <aside
                className={panelClassName}
                id="bulk-actions-panel"
            >
                <div className={styles.bulkActionsHeader}>
                    <h2>Bulk Actions</h2>
                    <button
                        type="button"
                        className={styles.bulkActionsClose}
                        onClick={() => setBulkActionsOpen(false)}
                        aria-label="Close bulk actions panel"
                    >
                        Close
                    </button>
                </div>
                <div className={styles.bulkActionsMeta}>
                    <span>{selectedServices.size} service{selectedServices.size === 1 ? '' : 's'} selected</span>
                    {activeAction && (
                        <span className={styles.activeActionHint}>{activeAction.label}</span>
                    )}
                </div>
                <div className={styles.bulkActionsContent}>
                    <div className={styles.bulkActionSearch}>
                        <div className={styles.bulkActionSearchField}>
                            <Search size={14} strokeWidth={1.6} className={styles.bulkActionSearchIcon} />
                            <input
                                id="bulk-actions-search"
                                type="search"
                                placeholder="Search actions..."
                                autoComplete="off"
                                value={actionSearchTerm}
                                onChange={(event) => setActionSearchTerm(event.target.value)}
                                className={styles.bulkActionSearchInput}
                                aria-label="Search bulk actions"
                            />
                        </div>
                    </div>

                    {isPriceAdjustmentAction && activeAction && (
                        <div className={styles.bulkActionDetail}>
                            <header className={styles.detailHeader}>
                                <h3>{activeAction.label}</h3>
                                <p className={styles.detailDescription}>
                                    {activeActionId === 'price-increase'
                                        ? 'Increase base prices for the selected services by a percentage or fixed amount.'
                                        : 'Decrease base prices for the selected services by a percentage or fixed amount.'}
                                </p>
                            </header>
                            <div className={styles.detailStats}>
                                <span>{selectedServices.size} service{selectedServices.size === 1 ? '' : 's'} selected</span>
                            </div>
                            {selectedServices.size === 0 && (
                                <p className={styles.noticeText}>Select one or more services from the table to enable this action.</p>
                            )}
                            <form className={styles.detailForm} onSubmit={applyPriceAdjustment}>
                                <div className={styles.fieldGroup}>
                                    <span className={styles.fieldLabel}>Adjustment Type</span>
                                    <div className={styles.segmentedControl}>
                                        <button
                                            type="button"
                                            className={[styles.segmentedButton, priceAdjustmentMode === 'percentage' ? styles.segmentedButtonActive : ''].filter(Boolean).join(' ')}
                                            onClick={() => {
                                                setPriceAdjustmentMode('percentage')
                                                setPriceAdjustmentError('')
                                            }}
                                        >
                                            Percentage
                                        </button>
                                        <button
                                            type="button"
                                            className={[styles.segmentedButton, priceAdjustmentMode === 'amount' ? styles.segmentedButtonActive : ''].filter(Boolean).join(' ')}
                                            onClick={() => {
                                                setPriceAdjustmentMode('amount')
                                                setPriceAdjustmentError('')
                                            }}
                                        >
                                            Fixed amount
                                        </button>
                                    </div>
                                </div>
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel} htmlFor="price-adjustment-value">
                                        {priceAdjustmentMode === 'percentage' ? 'Percentage (%)' : 'Amount (₹)'}
                                    </label>
                                    <div className={styles.fieldRow}>
                                        <input
                                            id="price-adjustment-value"
                                            type="number"
                                            min="0"
                                            step="0.1"
                                            inputMode="decimal"
                                            value={priceAdjustmentValue}
                                            onChange={(event) => {
                                                setPriceAdjustmentValue(event.target.value)
                                                setPriceAdjustmentError('')
                                            }}
                                            className={styles.adjustInput}
                                            placeholder={priceAdjustmentMode === 'percentage' ? 'e.g. 10' : 'e.g. 50'}
                                        />
                                        <span className={styles.fieldSuffix}>
                                            {priceAdjustmentMode === 'percentage' ? '%' : '₹'}
                                        </span>
                                    </div>
                                    <p className={styles.helperText}>
                                        {priceAdjustmentMode === 'percentage'
                                            ? 'Applies the selected percentage to each service price.'
                                            : 'Adjusts each service by the fixed amount.'}{' '}
                                        {activeActionId === 'price-decrease'
                                            ? 'Values are clamped so prices never drop below ₹0.'
                                            : 'Updates appear instantly in the table.'}
                                    </p>
                                </div>

                                {priceAdjustmentError && (
                                    <p className={styles.errorMessage}>{priceAdjustmentError}</p>
                                )}

                                {priceAdjustmentSummary && priceAdjustmentSummary.count > 0 && (
                                    <p className={styles.successMessage}>
                                        {priceAdjustmentSummary.direction === 'increase' ? 'Increased' : 'Decreased'}
                                        {' '}
                                        {priceAdjustmentSummary.count}
                                        {priceAdjustmentSummary.totalSelected && priceAdjustmentSummary.totalSelected !== priceAdjustmentSummary.count
                                            ? ` of ${priceAdjustmentSummary.totalSelected}`
                                            : ''}{' '}
                                        service{priceAdjustmentSummary.count === 1 ? '' : 's'} by{' '}
                                        {formatAdjustmentValue(priceAdjustmentSummary.value, priceAdjustmentSummary.mode)}
                                    </p>
                                )}

                                <div className={styles.detailFooter}>
                                    <button
                                        type="button"
                                        className={styles.secondaryActionButton}
                                        onClick={() => setActiveActionId(null)}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className={styles.primaryActionButton}
                                        disabled={
                                            !isPriceAdjustmentAction ||
                                            selectedServices.size === 0 ||
                                            !isPriceAdjustmentValueValid
                                        }
                                    >
                                        {activeActionId === 'price-increase' ? 'Apply Increase' : 'Apply Decrease'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {isRoundingAction && activeAction && (
                        <div className={styles.bulkActionDetail}>
                            <header className={styles.detailHeader}>
                                <h3>{activeAction.label}</h3>
                                <p className={styles.detailDescription}>
                                    Round base prices for the selected services to a consistent interval.
                                </p>
                            </header>
                            <div className={styles.detailStats}>
                                <span>{selectedServices.size} service{selectedServices.size === 1 ? '' : 's'} selected</span>
                            </div>
                            {selectedServices.size === 0 && (
                                <p className={styles.noticeText}>Select one or more services from the table to enable this action.</p>
                            )}
                            <form className={styles.detailForm} onSubmit={applyPriceRounding}>
                                <div className={styles.fieldGroup}>
                                    <span className={styles.fieldLabel}>Rounding Method</span>
                                    <div className={styles.segmentedControl}>
                                        <button
                                            type="button"
                                            className={[styles.segmentedButton, roundingMode === 'nearest' ? styles.segmentedButtonActive : ''].filter(Boolean).join(' ')}
                                            onClick={() => {
                                                setRoundingMode('nearest')
                                                setRoundingError('')
                                            }}
                                        >
                                            Nearest
                                        </button>
                                        <button
                                            type="button"
                                            className={[styles.segmentedButton, roundingMode === 'up' ? styles.segmentedButtonActive : ''].filter(Boolean).join(' ')}
                                            onClick={() => {
                                                setRoundingMode('up')
                                                setRoundingError('')
                                            }}
                                        >
                                            Round up
                                        </button>
                                        <button
                                            type="button"
                                            className={[styles.segmentedButton, roundingMode === 'down' ? styles.segmentedButtonActive : ''].filter(Boolean).join(' ')}
                                            onClick={() => {
                                                setRoundingMode('down')
                                                setRoundingError('')
                                            }}
                                        >
                                            Round down
                                        </button>
                                    </div>
                                </div>
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel} htmlFor="rounding-step-value">
                                        Interval size (₹)
                                    </label>
                                    <div className={styles.fieldRow}>
                                        <input
                                            id="rounding-step-value"
                                            type="number"
                                            min="0"
                                            step="1"
                                            inputMode="decimal"
                                            value={roundingStep}
                                            onChange={(event) => {
                                                setRoundingStep(event.target.value)
                                                setRoundingError('')
                                            }}
                                            className={styles.adjustInput}
                                            placeholder="e.g. 10"
                                        />
                                        <span className={styles.fieldSuffix}>₹</span>
                                    </div>
                                    <p className={styles.helperText}>
                                        Choose the rupee interval to snap prices to. Prices are clamped to ₹0 when rounding down.
                                    </p>
                                </div>

                                <div className={styles.fieldGroup}>
                                    <span className={styles.fieldLabel}>Quick picks</span>
                                    <div className={styles.presetButtons}>
                                        {ROUNDING_PRESETS.map((preset) => (
                                            <button
                                                key={preset}
                                                type="button"
                                                className={[styles.presetButton, roundingStep === preset ? styles.presetButtonActive : ''].filter(Boolean).join(' ')}
                                                onClick={() => {
                                                    setRoundingStep(preset)
                                                    setRoundingError('')
                                                }}
                                            >
                                                ₹{preset}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {roundingError && (
                                    <p className={styles.errorMessage}>{roundingError}</p>
                                )}

                                {roundingSummary && roundingSummary.count > 0 && (
                                    <p className={styles.successMessage}>
                                        Rounded {roundingSummary.count}
                                        {roundingSummary.totalSelected && roundingSummary.totalSelected !== roundingSummary.count
                                            ? ` of ${roundingSummary.totalSelected}`
                                            : ''}{' '}
                                        service{roundingSummary.count === 1 ? '' : 's'} {ROUNDING_MODE_SUMMARY[roundingSummary.mode]} ₹{roundingSummary.step}
                                    </p>
                                )}

                                <div className={styles.detailFooter}>
                                    <button
                                        type="button"
                                        className={styles.secondaryActionButton}
                                        onClick={() => setActiveActionId(null)}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className={styles.primaryActionButton}
                                        disabled={!isRoundingAction || selectedServices.size === 0 || !isRoundingStepValid}
                                    >
                                        Apply Rounding
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {isDurationSetAction && activeAction && (
                        <div className={styles.bulkActionDetail}>
                            <header className={styles.detailHeader}>
                                <h3>{activeAction.label}</h3>
                                <p className={styles.detailDescription}>
                                    Assign the same service duration (in minutes) to every selected service.
                                </p>
                            </header>
                            <div className={styles.detailStats}>
                                <span>{selectedServices.size} service{selectedServices.size === 1 ? '' : 's'} selected</span>
                            </div>
                            {selectedServices.size === 0 && (
                                <p className={styles.noticeText}>Select one or more services from the table to enable this action.</p>
                            )}
                            <form className={styles.detailForm} onSubmit={applyDurationSet}>
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel} htmlFor="duration-set-value">
                                        Duration (minutes)
                                    </label>
                                    <div className={styles.fieldRow}>
                                        <input
                                            id="duration-set-value"
                                            type="number"
                                            min="5"
                                            step="5"
                                            inputMode="numeric"
                                            value={durationValue}
                                            onChange={(event) => {
                                                setDurationValue(event.target.value)
                                                setDurationError('')
                                            }}
                                            className={styles.adjustInput}
                                            placeholder="e.g. 45"
                                        />
                                        <span className={styles.fieldSuffix}>min</span>
                                    </div>
                                    <p className={styles.helperText}>
                                        Use whole minutes only. Existing overrides will update immediately within the table.
                                    </p>
                                </div>

                                <div className={styles.fieldGroup}>
                                    <span className={styles.fieldLabel}>Quick picks</span>
                                    <div className={styles.presetButtons}>
                                        {DURATION_PRESETS.map((preset) => (
                                            <button
                                                key={preset}
                                                type="button"
                                                className={[styles.presetButton, durationValue === preset ? styles.presetButtonActive : ''].filter(Boolean).join(' ')}
                                                onClick={() => {
                                                    setDurationValue(preset)
                                                    setDurationError('')
                                                }}
                                            >
                                                {preset} min
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {durationError && (
                                    <p className={styles.errorMessage}>{durationError}</p>
                                )}

                                {durationSummary && durationSummary.count > 0 && (
                                    <p className={styles.successMessage}>
                                        Set duration to {durationSummary.value} min for {durationSummary.count}
                                        {durationSummary.totalSelected && durationSummary.totalSelected !== durationSummary.count
                                            ? ` of ${durationSummary.totalSelected}`
                                            : ''}{' '}
                                        service{durationSummary.count === 1 ? '' : 's'}.
                                    </p>
                                )}

                                <div className={styles.detailFooter}>
                                    <button
                                        type="button"
                                        className={styles.secondaryActionButton}
                                        onClick={() => setActiveActionId(null)}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className={styles.primaryActionButton}
                                        disabled={!isDurationSetAction || selectedServices.size === 0 || !isDurationValueValid}
                                    >
                                        Set Duration
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {isDurationIncreaseAction && activeAction && (
                        <div className={styles.bulkActionDetail}>
                            <header className={styles.detailHeader}>
                                <h3>{activeAction.label}</h3>
                                <p className={styles.detailDescription}>
                                    Add the same number of minutes to every selected service’s duration.
                                </p>
                            </header>
                            <div className={styles.detailStats}>
                                <span>{selectedServices.size} service{selectedServices.size === 1 ? '' : 's'} selected</span>
                            </div>
                            {selectedServices.size === 0 && (
                                <p className={styles.noticeText}>Select one or more services from the table to enable this action.</p>
                            )}
                            <form className={styles.detailForm} onSubmit={applyDurationIncrease}>
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel} htmlFor="duration-increase-value">
                                        Minutes to add
                                    </label>
                                    <div className={styles.fieldRow}>
                                        <input
                                            id="duration-increase-value"
                                            type="number"
                                            min="1"
                                            step="1"
                                            inputMode="numeric"
                                            value={durationIncreaseValue}
                                            onChange={(event) => {
                                                setDurationIncreaseValue(event.target.value)
                                                setDurationIncreaseError('')
                                            }}
                                            className={styles.adjustInput}
                                            placeholder="e.g. 10"
                                        />
                                        <span className={styles.fieldSuffix}>min</span>
                                    </div>
                                    <p className={styles.helperText}>
                                        Only services that already have a duration will be updated.
                                    </p>
                                </div>

                                {durationIncreaseError && (
                                    <p className={styles.errorMessage}>{durationIncreaseError}</p>
                                )}

                                {durationIncreaseSummary && durationIncreaseSummary.count > 0 && (
                                    <div className={styles.successMessage}>
                                        <p>
                                            Added {durationIncreaseSummary.increment} min to {durationIncreaseSummary.count}
                                            {durationIncreaseSummary.totalSelected && durationIncreaseSummary.totalSelected !== durationIncreaseSummary.count
                                                ? ` of ${durationIncreaseSummary.totalSelected}`
                                                : ''}{' '}
                                            service{durationIncreaseSummary.count === 1 ? '' : 's'}.
                                        </p>
                                    </div>
                                )}

                                <div className={styles.detailFooter}>
                                    <button
                                        type="button"
                                        className={styles.secondaryActionButton}
                                        onClick={() => setActiveActionId(null)}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className={styles.primaryActionButton}
                                        disabled={!isDurationIncreaseAction || selectedServices.size === 0 || !isDurationIncreaseValueValid}
                                    >
                                        Increase Duration
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {isDurationDecreaseAction && activeAction && (
                        <div className={styles.bulkActionDetail}>
                            <header className={styles.detailHeader}>
                                <h3>{activeAction.label}</h3>
                                <p className={styles.detailDescription}>
                                    Subtract a fixed number of minutes from each selected service’s duration without dropping below zero.
                                </p>
                            </header>
                            <div className={styles.detailStats}>
                                <span>{selectedServices.size} service{selectedServices.size === 1 ? '' : 's'} selected</span>
                            </div>
                            {selectedServices.size === 0 && (
                                <p className={styles.noticeText}>Select one or more services from the table to enable this action.</p>
                            )}
                            <form className={styles.detailForm} onSubmit={applyDurationDecrease}>
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel} htmlFor="duration-decrease-value">
                                        Minutes to subtract
                                    </label>
                                    <div className={styles.fieldRow}>
                                        <input
                                            id="duration-decrease-value"
                                            type="number"
                                            min="1"
                                            step="1"
                                            inputMode="numeric"
                                            value={durationDecreaseValue}
                                            onChange={(event) => {
                                                setDurationDecreaseValue(event.target.value)
                                                setDurationDecreaseError('')
                                            }}
                                            className={styles.adjustInput}
                                            placeholder="e.g. 10"
                                        />
                                        <span className={styles.fieldSuffix}>min</span>
                                    </div>
                                    <p className={styles.helperText}>
                                        Services without a duration remain unchanged. Resulting durations never go below 0.
                                    </p>
                                </div>

                                {durationDecreaseError && (
                                    <p className={styles.errorMessage}>{durationDecreaseError}</p>
                                )}

                                {durationDecreaseSummary && durationDecreaseSummary.count > 0 && (
                                    <div className={styles.successMessage}>
                                        <p>
                                            Reduced by {durationDecreaseSummary.decrement} min for {durationDecreaseSummary.count}
                                            {durationDecreaseSummary.totalSelected && durationDecreaseSummary.totalSelected !== durationDecreaseSummary.count
                                                ? ` of ${durationDecreaseSummary.totalSelected}`
                                                : ''}{' '}
                                            service{durationDecreaseSummary.count === 1 ? '' : 's'}.
                                        </p>
                                    </div>
                                )}

                                <div className={styles.detailFooter}>
                                    <button
                                        type="button"
                                        className={styles.secondaryActionButton}
                                        onClick={() => setActiveActionId(null)}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className={styles.primaryActionButton}
                                        disabled={!isDurationDecreaseAction || selectedServices.size === 0 || !isDurationDecreaseValueValid}
                                    >
                                        Decrease Duration
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {isDiscountApplyAction && activeAction && (
                        <div className={styles.bulkActionDetail}>
                            <header className={styles.detailHeader}>
                                <h3>{activeAction.label}</h3>
                                <p className={styles.detailDescription}>
                                    Set a consistent offer percentage for every selected service at once.
                                </p>
                            </header>
                            <div className={styles.detailStats}>
                                <span>{selectedServices.size} service{selectedServices.size === 1 ? '' : 's'} selected</span>
                            </div>
                            {selectedServices.size === 0 && (
                                <p className={styles.noticeText}>Select one or more services from the table to enable this action.</p>
                            )}
                            <form className={styles.detailForm} onSubmit={applyDiscount}>
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel} htmlFor="discount-percentage-value">
                                        Discount percentage (%)
                                    </label>
                                    <div className={styles.fieldRow}>
                                        <input
                                            id="discount-percentage-value"
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.5"
                                            inputMode="decimal"
                                            value={discountValue}
                                            onChange={(event) => {
                                                setDiscountValue(event.target.value)
                                                setDiscountError('')
                                            }}
                                            className={styles.adjustInput}
                                            placeholder="e.g. 15"
                                        />
                                        <span className={styles.fieldSuffix}>%</span>
                                    </div>
                                    <p className={styles.helperText}>
                                        Enter a value between 0 and 100. The offer percentage will reflect across all selected services.
                                    </p>
                                </div>

                                <div className={styles.fieldGroup}>
                                    <span className={styles.fieldLabel}>Quick picks</span>
                                    <div className={styles.presetButtons}>
                                        {DISCOUNT_PRESETS.map((preset) => (
                                            <button
                                                key={preset}
                                                type="button"
                                                className={[styles.presetButton, discountValue === preset ? styles.presetButtonActive : ''].filter(Boolean).join(' ')}
                                                onClick={() => {
                                                    setDiscountValue(preset)
                                                    setDiscountError('')
                                                }}
                                            >
                                                {preset}%
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {discountError && (
                                    <p className={styles.errorMessage}>{discountError}</p>
                                )}

                                {discountSummary && discountSummary.count > 0 && (
                                    <p className={styles.successMessage}>
                                        Applied {Number.isFinite(discountSummary.value)
                                            ? (Number.isInteger(discountSummary.value)
                                                ? discountSummary.value.toFixed(0)
                                                : discountSummary.value.toFixed(2))
                                            : discountSummary.value}%
                                        {' '}discount to {discountSummary.count}
                                        {discountSummary.totalSelected && discountSummary.totalSelected !== discountSummary.count
                                            ? ` of ${discountSummary.totalSelected}`
                                            : ''}{' '}
                                        service{discountSummary.count === 1 ? '' : 's'}.
                                    </p>
                                )}

                                <div className={styles.detailFooter}>
                                    <button
                                        type="button"
                                        className={styles.secondaryActionButton}
                                        onClick={() => setActiveActionId(null)}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className={styles.primaryActionButton}
                                        disabled={!isDiscountApplyAction || selectedServices.size === 0 || !isDiscountValueValid}
                                    >
                                        Apply Discount
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {isDiscountRemoveAction && activeAction && (
                        <div className={styles.bulkActionDetail}>
                            <header className={styles.detailHeader}>
                                <h3>{activeAction.label}</h3>
                                <p className={styles.detailDescription}>
                                    Clear the offer percentage so the selected services have no discount applied.
                                </p>
                            </header>
                            <div className={styles.detailStats}>
                                <span>{selectedServices.size} service{selectedServices.size === 1 ? '' : 's'} selected</span>
                            </div>
                            {selectedServices.size === 0 && (
                                <p className={styles.noticeText}>Select one or more services from the table to enable this action.</p>
                            )}
                            <form className={styles.detailForm} onSubmit={applyDiscountRemoval}>
                                <div className={styles.fieldGroup}>
                                    <p className={styles.helperText}>
                                        Removes any existing offer percentage. You can reapply a discount later from the drawer or by editing individual rows.
                                    </p>
                                </div>

                                {discountRemoveError && (
                                    <p className={styles.errorMessage}>{discountRemoveError}</p>
                                )}

                                {discountRemoveSummary && discountRemoveSummary.count > 0 && (
                                    <p className={styles.successMessage}>
                                        Cleared discounts for {discountRemoveSummary.count}
                                        {discountRemoveSummary.totalSelected && discountRemoveSummary.totalSelected !== discountRemoveSummary.count
                                            ? ` of ${discountRemoveSummary.totalSelected}`
                                            : ''}{' '}
                                        service{discountRemoveSummary.count === 1 ? '' : 's'}.
                                    </p>
                                )}

                                <div className={styles.detailFooter}>
                                    <button
                                        type="button"
                                        className={styles.secondaryActionButton}
                                        onClick={() => setActiveActionId(null)}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className={styles.primaryActionButton}
                                        disabled={!isDiscountRemoveAction || selectedServices.size === 0}
                                    >
                                        Remove Discount
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {isNameStandardizeAction && activeAction && (
                        <div className={styles.bulkActionDetail}>
                            <header className={styles.detailHeader}>
                                <h3>{activeAction.label}</h3>
                                <p className={styles.detailDescription}>
                                    Clean up spacing and apply a consistent casing style across every selected service name.
                                </p>
                            </header>
                            <div className={styles.detailStats}>
                                <span>{selectedServices.size} service{selectedServices.size === 1 ? '' : 's'} selected</span>
                            </div>
                            {selectedServices.size === 0 && (
                                <p className={styles.noticeText}>Select one or more services from the table to enable this action.</p>
                            )}
                            <form className={styles.detailForm} onSubmit={applyNameStandardization}>
                                <div className={styles.fieldGroup}>
                                    <span className={styles.fieldLabel}>Case style</span>
                                    <div className={styles.segmentedControl}>
                                        {NAME_STANDARDIZE_CASES.map((option) => (
                                            <button
                                                key={option.id}
                                                type="button"
                                                className={[
                                                    styles.segmentedButton,
                                                    nameStandardizeCase === option.id ? styles.segmentedButtonActive : '',
                                                ].filter(Boolean).join(' ')}
                                                onClick={() => {
                                                    setNameStandardizeCase(option.id)
                                                    setNameStandardizeError('')
                                                }}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                    <p className={styles.helperText}>
                                        Choose how service names should be capitalized after clean-up.
                                    </p>
                                </div>

                                <div className={styles.fieldGroup}>
                                    <span className={styles.fieldLabel}>Clean-up rules</span>
                                    <div className={styles.checkboxList}>
                                        <label className={styles.checkboxLabel}>
                                            <input
                                                type="checkbox"
                                                className={styles.checkboxInput}
                                                checked={nameStandardizeTrimEdges}
                                                onChange={(event) => {
                                                    setNameStandardizeTrimEdges(event.target.checked)
                                                    setNameStandardizeError('')
                                                }}
                                            />
                                            Trim leading and trailing spaces
                                        </label>
                                        <label className={styles.checkboxLabel}>
                                            <input
                                                type="checkbox"
                                                className={styles.checkboxInput}
                                                checked={nameStandardizeCollapseSpaces}
                                                onChange={(event) => {
                                                    setNameStandardizeCollapseSpaces(event.target.checked)
                                                    setNameStandardizeError('')
                                                }}
                                            />
                                            Collapse repeated spaces between words
                                        </label>
                                    </div>
                                    <p className={styles.helperText}>
                                        Enable whichever spacing adjustments you need before the casing style is applied.
                                    </p>
                                </div>

                                {nameStandardizeError && (
                                    <p className={styles.errorMessage}>{nameStandardizeError}</p>
                                )}

                                {nameStandardizeSummary && nameStandardizeSummary.count > 0 && (
                                    <div className={styles.successMessage}>
                                        <p>
                                            Standardized names for {nameStandardizeSummary.count}
                                            {nameStandardizeSummary.totalSelected && nameStandardizeSummary.totalSelected !== nameStandardizeSummary.count
                                                ? ` of ${nameStandardizeSummary.totalSelected}`
                                                : ''}{' '}
                                            service{nameStandardizeSummary.count === 1 ? '' : 's'} using {nameStandardizeSummary.caseMode}.
                                        </p>
                                        {(nameStandardizeSummary.trimmed || nameStandardizeSummary.collapsed) && (
                                            <p>
                                                Cleanup applied: {
                                                    [
                                                        nameStandardizeSummary.trimmed ? 'trim whitespace' : null,
                                                        nameStandardizeSummary.collapsed ? 'collapse extra spaces' : null,
                                                    ].filter(Boolean).join(', ')
                                                }.
                                            </p>
                                        )}
                                    </div>
                                )}

                                <div className={styles.detailFooter}>
                                    <button
                                        type="button"
                                        className={styles.secondaryActionButton}
                                        onClick={() => setActiveActionId(null)}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className={styles.primaryActionButton}
                                        disabled={!isNameStandardizeAction || selectedServices.size === 0 || !isNameStandardizeConfigured}
                                    >
                                        Standardize Names
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {isNameAffixAction && activeAction && (
                        <div className={styles.bulkActionDetail}>
                            <header className={styles.detailHeader}>
                                <h3>{activeAction.label}</h3>
                                <p className={styles.detailDescription}>
                                    Add a consistent prefix and/or suffix to each selected service name without duplicating what is already there.
                                </p>
                            </header>
                            <div className={styles.detailStats}>
                                <span>{selectedServices.size} service{selectedServices.size === 1 ? '' : 's'} selected</span>
                            </div>
                            {selectedServices.size === 0 && (
                                <p className={styles.noticeText}>Select one or more services from the table to enable this action.</p>
                            )}
                            <form className={styles.detailForm} onSubmit={applyNameAffix}>
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel} htmlFor="name-affix-prefix">
                                        Prefix
                                    </label>
                                    <input
                                        id="name-affix-prefix"
                                        type="text"
                                        value={nameAffixPrefix}
                                        onChange={(event) => {
                                            setNameAffixPrefix(event.target.value)
                                            setNameAffixError('')
                                        }}
                                        className={styles.input}
                                        placeholder="e.g. VIP "
                                    />
                                    <p className={styles.helperText}>
                                        Leave blank to skip. Spaces are preserved exactly as typed.
                                    </p>
                                </div>

                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel} htmlFor="name-affix-suffix">
                                        Suffix
                                    </label>
                                    <input
                                        id="name-affix-suffix"
                                        type="text"
                                        value={nameAffixSuffix}
                                        onChange={(event) => {
                                            setNameAffixSuffix(event.target.value)
                                            setNameAffixError('')
                                        }}
                                        className={styles.input}
                                        placeholder="e.g. (Add-on)"
                                    />
                                    <p className={styles.helperText}>
                                        Combine with the prefix or use alone. Include your own spacing or punctuation.
                                    </p>
                                </div>

                                {nameAffixError && (
                                    <p className={styles.errorMessage}>{nameAffixError}</p>
                                )}

                                {nameAffixSummary && nameAffixSummary.count > 0 && (
                                    <div className={styles.successMessage}>
                                        <p>
                                            Applied {[
                                                nameAffixSummary.prefixApplied ? 'prefix' : null,
                                                nameAffixSummary.suffixApplied ? 'suffix' : null,
                                            ].filter(Boolean).join(' & ') || 'changes'} for {nameAffixSummary.count}
                                            {nameAffixSummary.totalSelected && nameAffixSummary.totalSelected !== nameAffixSummary.count
                                                ? ` of ${nameAffixSummary.totalSelected}`
                                                : ''}{' '}
                                            service{nameAffixSummary.count === 1 ? '' : 's'}.
                                        </p>
                                        {nameAffixSummary.examples && nameAffixSummary.examples.length > 0 && (
                                            <ul className={styles.successDetailsList}>
                                                {nameAffixSummary.examples.map((example, index) => (
                                                    <li key={`${example.identifier}-${index}`} className={styles.successDetailsItem}>
                                                        "{example.from || 'Unnamed'}" → "{example.to}"
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}

                                <div className={styles.detailFooter}>
                                    <button
                                        type="button"
                                        className={styles.secondaryActionButton}
                                        onClick={() => setActiveActionId(null)}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className={styles.primaryActionButton}
                                        disabled={!isNameAffixAction || selectedServices.size === 0 || !isNameAffixConfigured}
                                    >
                                        Apply Prefix/Suffix
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {isNameReplaceAction && activeAction && (
                        <div className={styles.bulkActionDetail}>
                            <header className={styles.detailHeader}>
                                <h3>{activeAction.label}</h3>
                                <p className={styles.detailDescription}>
                                    Quickly replace text inside each selected service name. Matches are replaced everywhere in the name.
                                </p>
                            </header>
                            <div className={styles.detailStats}>
                                <span>{selectedServices.size} service{selectedServices.size === 1 ? '' : 's'} selected</span>
                            </div>
                            {selectedServices.size === 0 && (
                                <p className={styles.noticeText}>Select one or more services from the table to enable this action.</p>
                            )}
                            <form className={styles.detailForm} onSubmit={applyNameReplace}>
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel} htmlFor="name-replace-find">
                                        Text to find
                                    </label>
                                    <input
                                        id="name-replace-find"
                                        type="text"
                                        value={nameReplaceFindValue}
                                        onChange={(event) => {
                                            setNameReplaceFindValue(event.target.value)
                                            setNameReplaceError('')
                                        }}
                                        className={styles.input}
                                        placeholder="e.g. Facial"
                                        required
                                    />
                                    <p className={styles.helperText}>
                                        Required. Matches are literal; escape sequences or wildcards are not used.
                                    </p>
                                </div>

                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel} htmlFor="name-replace-replace">
                                        Replace with
                                    </label>
                                    <input
                                        id="name-replace-replace"
                                        type="text"
                                        value={nameReplaceReplaceValue}
                                        onChange={(event) => {
                                            setNameReplaceReplaceValue(event.target.value)
                                            setNameReplaceError('')
                                        }}
                                        className={styles.input}
                                        placeholder="e.g. Skin Treatment"
                                    />
                                    <p className={styles.helperText}>
                                        Leave blank to remove the matched text entirely.
                                    </p>
                                </div>

                                <div className={styles.fieldGroup}>
                                    <span className={styles.fieldLabel}>Options</span>
                                    <label className={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            className={styles.checkboxInput}
                                            checked={nameReplaceCaseSensitive}
                                            onChange={(event) => {
                                                setNameReplaceCaseSensitive(event.target.checked)
                                                setNameReplaceError('')
                                            }}
                                        />
                                        Match case
                                    </label>
                                    <p className={styles.helperText}>
                                        Unchecked replaces matches regardless of casing.
                                    </p>
                                </div>

                                {nameReplaceError && (
                                    <p className={styles.errorMessage}>{nameReplaceError}</p>
                                )}

                                {nameReplaceSummary && nameReplaceSummary.count > 0 && (
                                    <div className={styles.successMessage}>
                                        <p>
                                            Replaced "{nameReplaceSummary.findValue}" with "{nameReplaceSummary.replaceValue}" for {nameReplaceSummary.count}
                                            {nameReplaceSummary.totalSelected && nameReplaceSummary.totalSelected !== nameReplaceSummary.count
                                                ? ` of ${nameReplaceSummary.totalSelected}`
                                                : ''}{' '}
                                            service{nameReplaceSummary.count === 1 ? '' : 's'}{nameReplaceSummary.caseSensitive ? ' (case sensitive).' : '.'}
                                        </p>
                                        {nameReplaceSummary.examples && nameReplaceSummary.examples.length > 0 && (
                                            <ul className={styles.successDetailsList}>
                                                {nameReplaceSummary.examples.map((example, index) => (
                                                    <li key={`${example.identifier}-${index}`} className={styles.successDetailsItem}>
                                                        "{example.from || 'Unnamed'}" → "{example.to}"
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}

                                <div className={styles.detailFooter}>
                                    <button
                                        type="button"
                                        className={styles.secondaryActionButton}
                                        onClick={() => setActiveActionId(null)}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className={styles.primaryActionButton}
                                        disabled={!isNameReplaceAction || selectedServices.size === 0 || !isNameReplaceConfigured}
                                    >
                                        Replace Text
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {isDuplicateAnalysisAction && activeAction && (
                        <div className={styles.bulkActionDetail}>
                            <header className={styles.detailHeader}>
                                <h3>{activeAction.label}</h3>
                                <p className={styles.detailDescription}>
                                    Scan service names to spot duplicates so you can clean them up quickly.
                                </p>
                            </header>
                            <div className={styles.detailStats}>
                                <span>{totalServices} total service{totalServices === 1 ? '' : 's'} in list</span>
                                <span>{selectedServices.size} selected</span>
                            </div>
                            <form className={styles.detailForm} onSubmit={analyzeDuplicateServiceNames}>
                                <div className={styles.fieldGroup}>
                                    <span className={styles.fieldLabel}>Match options</span>
                                    <label className={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            className={styles.checkboxInput}
                                            checked={duplicateTrimWhitespace}
                                            onChange={(event) => {
                                                setDuplicateTrimWhitespace(event.target.checked)
                                                setDuplicateError('')
                                            }}
                                        />
                                        Ignore extra spaces
                                    </label>
                                    <label className={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            className={styles.checkboxInput}
                                            checked={duplicateCaseSensitive}
                                            onChange={(event) => {
                                                setDuplicateCaseSensitive(event.target.checked)
                                                setDuplicateError('')
                                            }}
                                        />
                                        Match case exactly
                                    </label>
                                    <label className={styles.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            className={styles.checkboxInput}
                                            checked={duplicateSelectionOnly}
                                            onChange={(event) => {
                                                setDuplicateSelectionOnly(event.target.checked)
                                                setDuplicateError('')
                                            }}
                                            disabled={selectedServices.size === 0}
                                        />
                                        Only check selected rows
                                    </label>
                                    {selectedServices.size === 0 && duplicateSelectionOnly && (
                                        <p className={styles.helperText}>
                                            Select services to enable this option or turn it off to scan everything.
                                        </p>
                                    )}
                                </div>

                                {duplicateError && (
                                    <p className={styles.errorMessage}>{duplicateError}</p>
                                )}

                                {duplicateSummary && duplicateSummary.duplicateNameCount > 0 && (
                                    <div className={styles.successMessage}>
                                        <p>
                                            Found {duplicateSummary.duplicateNameCount} duplicate name{duplicateSummary.duplicateNameCount === 1 ? '' : 's'} involving {duplicateSummary.duplicateServiceCount} service{duplicateSummary.duplicateServiceCount === 1 ? '' : 's'} (scanned {duplicateSummary.totalConsidered}).
                                            {duplicateSummary.selectionOnly ? ' Scoped to selected rows.' : ''}
                                        </p>
                                        <p>
                                            Mode: {duplicateSummary.caseSensitive ? 'case sensitive' : 'case insensitive'}; {duplicateSummary.trimWhitespace ? 'ignores extra spaces' : 'keeps original spacing'}.
                                        </p>
                                        {duplicateSummary.duplicates && duplicateSummary.duplicates.length > 0 && (
                                            <ul className={styles.analysisList}>
                                                {duplicateSummary.duplicates.slice(0, 5).map((entry, index) => {
                                                    const sample = entry.services.slice(0, 3).map(service => service.categoryName || 'Uncategorised').join(', ')
                                                    const remaining = entry.services.length - 3
                                                    return (
                                                        <li key={`${entry.displayName || 'unnamed'}-${index}`} className={styles.analysisListItem}>
                                                            <strong>{entry.displayName || '(blank name)'}</strong> — {entry.count} service{entry.count === 1 ? '' : 's'}
                                                            {sample ? ` · Categories: ${sample}${remaining > 0 ? ` +${remaining} more` : ''}` : ''}
                                                        </li>
                                                    )
                                                })}
                                            </ul>
                                        )}
                                    </div>
                                )}

                                <div className={styles.detailFooter}>
                                    <button
                                        type="button"
                                        className={styles.secondaryActionButton}
                                        onClick={() => setActiveActionId(null)}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className={styles.primaryActionButton}
                                        disabled={!isDuplicateAnalysisAction || totalServices === 0 || (duplicateSelectionOnly && selectedServices.size === 0)}
                                    >
                                        Find Duplicates
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {isHsnBulkUpdateAction && activeAction && (
                        <div className={styles.bulkActionDetail}>
                            <header className={styles.detailHeader}>
                                <h3>{activeAction.label}</h3>
                                <p className={styles.detailDescription}>
                                    Apply a predefined HSN/SAC code to every selected service in one go.
                                </p>
                            </header>
                            <div className={styles.detailStats}>
                                <span>{selectedServices.size} service{selectedServices.size === 1 ? '' : 's'} selected</span>
                                <span>{hsnCodes.length} code{hsnCodes.length === 1 ? '' : 's'} available</span>
                            </div>
                            {selectedServices.size === 0 && (
                                <p className={styles.noticeText}>Select one or more services from the table to enable this action.</p>
                            )}
                            <form className={styles.detailForm} onSubmit={applyHsnCodeUpdate}>
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel} htmlFor="hsn-code-select">
                                        HSN/SAC code
                                    </label>
                                    <Select
                                        inputId="hsn-code-select"
                                        className={styles.reactSelectContainer}
                                        classNamePrefix="hsnSelect"
                                        isClearable
                                        isLoading={isLoadingHsnCodes}
                                        options={hsnSelectOptions}
                                        value={selectedHsnOption}
                                        onChange={(option) => {
                                            setSelectedHsnCode(option ? option.value : '')
                                            setHsnUpdateError('')
                                        }}
                                        placeholder="Search or pick an HSN/SAC code"
                                        noOptionsMessage={() => (
                                            isLoadingHsnCodes
                                                ? 'Loading predefined codes…'
                                                : hsnCodes.length === 0
                                                    ? 'No codes available yet.'
                                                    : 'No codes match your search.'
                                        )}
                                        loadingMessage={() => 'Loading predefined codes…'}
                                    />
                                    <p className={styles.helperText}>
                                        Start typing to search your predefined list. Clear the selection to reset.
                                    </p>
                                </div>

                                {selectedHsnCodeDetail && (
                                    <p className={styles.helperText}>
                                        Selected code: {selectedHsnCodeDetail.code}
                                        {selectedHsnCodeDetail.description
                                            ? ` — ${selectedHsnCodeDetail.description}`
                                            : ''}
                                    </p>
                                )}

                                {hsnUpdateError && (
                                    <p className={styles.errorMessage}>{hsnUpdateError}</p>
                                )}

                                {hsnUpdateSummary && hsnUpdateSummary.count > 0 && (
                                    <p className={styles.successMessage}>
                                        Updated {hsnUpdateSummary.count}
                                        {hsnUpdateSummary.totalSelected && hsnUpdateSummary.totalSelected !== hsnUpdateSummary.count
                                            ? ` of ${hsnUpdateSummary.totalSelected}`
                                            : ''}{' '}
                                        service{hsnUpdateSummary.count === 1 ? '' : 's'} to HSN code {hsnUpdateSummary.code}
                                    </p>
                                )}

                                <div className={styles.detailFooter}>
                                    <button
                                        type="button"
                                        className={styles.secondaryActionButton}
                                        onClick={() => setActiveActionId(null)}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className={styles.primaryActionButton}
                                        disabled={!isHsnBulkUpdateAction || selectedServices.size === 0 || !isHsnSelectionValid}
                                    >
                                        Apply HSN Code
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {isHsnClearAction && activeAction && (
                        <div className={styles.bulkActionDetail}>
                            <header className={styles.detailHeader}>
                                <h3>{activeAction.label}</h3>
                                <p className={styles.detailDescription}>
                                    Remove the HSN/SAC code from each selected service so it is left blank.
                                </p>
                            </header>
                            <div className={styles.detailStats}>
                                <span>{selectedServices.size} service{selectedServices.size === 1 ? '' : 's'} selected</span>
                            </div>
                            {selectedServices.size === 0 && (
                                <p className={styles.noticeText}>Select one or more services from the table to enable this action.</p>
                            )}
                            <form className={styles.detailForm} onSubmit={applyHsnCodeClear}>
                                <div className={styles.fieldGroup}>
                                    <p className={styles.helperText}>
                                        This will set the HSN/SAC field to blank for every selected service. You can undo the change before saving by choosing a new code or using the table cells directly.
                                    </p>
                                </div>

                                {hsnClearError && (
                                    <p className={styles.errorMessage}>{hsnClearError}</p>
                                )}

                                {hsnClearSummary && hsnClearSummary.count > 0 && (
                                    <p className={styles.successMessage}>
                                        Cleared HSN codes for {hsnClearSummary.count}
                                        {hsnClearSummary.totalSelected && hsnClearSummary.totalSelected !== hsnClearSummary.count
                                            ? ` of ${hsnClearSummary.totalSelected}`
                                            : ''}{' '}
                                        service{hsnClearSummary.count === 1 ? '' : 's'}.
                                    </p>
                                )}

                                <div className={styles.detailFooter}>
                                    <button
                                        type="button"
                                        className={styles.secondaryActionButton}
                                        onClick={() => setActiveActionId(null)}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        className={styles.primaryActionButton}
                                        disabled={!isHsnClearAction || selectedServices.size === 0}
                                    >
                                        Clear HSN Codes
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    <div className={styles.bulkActionsScroller}>
                        {hasActionSearch && filteredActionGroups.length === 0 ? (
                            <div className={styles.bulkActionEmpty}>
                                <p>No actions match "{actionSearchTerm.trim()}".</p>
                                <button
                                    type="button"
                                    className={styles.bulkActionResetSearch}
                                    onClick={() => setActionSearchTerm('')}
                                >
                                    Clear search
                                </button>
                            </div>
                        ) : (
                            <div className={styles.bulkActionsGrid}>
                                {filteredActionGroups.map(group => {
                                    const GroupIcon = group.icon
                                    const isActionGroup = activeAction?.groupId === group.id
                                    const isExpanded = isActionGroup || expandedGroups.has(group.id)
                                    const actionsId = `bulk-actions-${group.id}`
                                    const countLabel = hasActionSearch
                                        ? `${group.actions.length} match${group.actions.length === 1 ? '' : 'es'}`
                                        : `${group.actions.length} action${group.actions.length === 1 ? '' : 's'}`

                                    return (
                                        <article key={group.id} className={styles.bulkActionGroup}>
                                            <button
                                                type="button"
                                                className={[styles.bulkActionGroupHeaderButton, isExpanded ? styles.bulkActionGroupHeaderButtonExpanded : ''].filter(Boolean).join(' ')}
                                                onClick={() => {
                                                    if (!hasActionSearch) {
                                                        handleToggleGroup(group.id)
                                                    }
                                                }}
                                                aria-expanded={isExpanded}
                                                aria-controls={actionsId}
                                                aria-disabled={hasActionSearch ? 'true' : undefined}
                                            >
                                                <div className={styles.groupIconWrapper}>
                                                    <GroupIcon size={16} strokeWidth={1.8} />
                                                </div>
                                                <span className={styles.bulkActionGroupTitle}>{group.title}</span>
                                                <span className={styles.bulkActionGroupMeta}>
                                                    <span className={styles.bulkActionGroupCount}>{countLabel}</span>
                                                    <ChevronDown size={16} strokeWidth={1.8} className={styles.bulkActionGroupChevron} />
                                                </span>
                                            </button>
                                            <div
                                                id={actionsId}
                                                className={styles.bulkActionButtons}
                                                hidden={!isExpanded}
                                                aria-hidden={!isExpanded}
                                            >
                                                {group.actions.map(action => {
                                                    const disabled = action.requiresSelection && selectedServices.size === 0
                                                    const isActive = activeActionId === action.id
                                                    const isImplemented = implementedActionIds.has(action.id)
                                                    const statusLabel = isImplemented ? 'Active' : 'Coming soon'
                                                    return (
                                                        <button
                                                            key={action.id}
                                                            type="button"
                                                            className={[styles.bulkActionButton, isActive ? styles.bulkActionButtonActive : ''].filter(Boolean).join(' ')}
                                                            onClick={() => handleBulkAction(action.id)}
                                                            disabled={disabled}
                                                            aria-pressed={isActive}
                                                            aria-label={`${action.label} - ${statusLabel}`}
                                                            title={`${action.label} - ${statusLabel}`}
                                                        >
                                                            <span className={styles.bulkActionLabelRow}>
                                                                <span
                                                                    className={[styles.actionStatusDot, isImplemented ? styles.actionStatusDotActive : styles.actionStatusDotPending].filter(Boolean).join(' ')}
                                                                    aria-hidden="true"
                                                                />
                                                                <span className={styles.bulkActionLabel}>{action.label}</span>
                                                                <span className={[styles.actionStatusText, isImplemented ? styles.actionStatusTextActive : styles.actionStatusTextPending].filter(Boolean).join(' ')}>{statusLabel}</span>
                                                            </span>
                                                            {action.description && (
                                                                <span className={styles.bulkActionDescription}>{action.description}</span>
                                                            )}
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                        </article>
                                    )
                                })}
                                <div className={styles.bulkActionFooter} aria-hidden="true">
                                    <strong>End of the magic.</strong>
                                    <span>That's all the actions for now - more spells coming soon.</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </aside>

            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerTop}>
                    <h1 className={styles.title}>Manage Pricelist</h1>
                    <div className={styles.actions}>
                        <button
                            type="button"
                            className={[styles.button, isBulkActionsOpen ? styles.buttonActive : ''].filter(Boolean).join(' ')}
                            onClick={() => setBulkActionsOpen(prev => !prev)}
                            aria-expanded={isBulkActionsOpen}
                            aria-controls="bulk-actions-panel"
                        >
                            <Wand2 size={16} />
                            Bulk Actions
                        </button>
                        {/* <button className={styles.button} onClick={handleExport}>
                            <Download size={16} />
                            Export
                        </button> */}
                        <button 
                            className={`${styles.button} ${styles.buttonPrimary}`}
                            onClick={handleBulkUpdate}
                            disabled={isSaveDisabled}
                        >
                            <Save size={16} />
                            Save Changes
                        </button>
                    </div>
                </div>
                <div className={styles.stats}>
                    <div className={styles.statItem}>
                        <span className={styles.statLabel}>Total Services:</span>
                        <span className={styles.statValue}>{totalServices}</span>
                    </div>
                    <div className={styles.statItem}>
                        <span className={styles.statLabel}>Categories:</span>
                        <span className={styles.statValue}>{activeCategories}</span>
                    </div>
                    <div className={styles.statItem}>
                        <span className={styles.statLabel}>Selected:</span>
                        <span className={styles.statValue}>{selectedServices.size}</span>
                    </div>
                    {Object.keys(editedServices).length > 0 && (
                        <div className={styles.statItem}>
                            <span className={styles.statLabel}>Edited:</span>
                            <span className={styles.statValue} style={{ color: '#f59e0b' }}>
                                {Object.keys(editedServices).length}
                            </span>
                        </div>
                    )}
                </div>

                {saveFeedback && (
                    <div
                        className={[
                            styles.saveBanner,
                            saveFeedback.type === 'success'
                                ? styles.saveBannerSuccess
                                : saveFeedback.type === 'error'
                                    ? styles.saveBannerError
                                    : styles.saveBannerInfo,
                        ].filter(Boolean).join(' ')}
                        role={saveFeedback.type === 'error' ? 'alert' : 'status'}
                    >
                        <span>{saveFeedback.text}</span>
                        <button
                            type="button"
                            className={styles.saveBannerClose}
                            aria-label="Dismiss message"
                            onClick={() => setSaveFeedback(null)}
                        >
                            <X size={14} strokeWidth={2} />
                        </button>
                    </div>
                )}
            </div>

            {/* Table */}
            <div className={styles.tableContainer}>
                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead className={styles.tableHeader}>
                            <tr>
                                <th className={styles.checkboxCell}>
                                    {(() => {
                                        const isAllSelected = selectedServices.size === totalServices && totalServices > 0
                                        const isPartiallySelected = selectedServices.size > 0 && selectedServices.size < totalServices
                                        const Icon = isAllSelected ? CheckSquare : isPartiallySelected ? MinusSquare : Square
                                        return (
                                            <button
                                                type="button"
                                                onClick={handleSelectAll}
                                                className={styles.iconCheckbox}
                                                aria-pressed={isAllSelected}
                                                aria-label="Select all services"
                                            >
                                                <Icon size={16} strokeWidth={1.9} />
                                            </button>
                                        )
                                    })()}
                                </th>
                                <th>Service Name</th>
                                <th>Price (₹)</th>
                                <th>Duration (min)</th>
                                <th>HSN Code</th>
                                <th>Offer %</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody className={styles.tableBody}>
                            {priceList.map((category) => {
                                if (!category.services || category.services.length === 0) {
                                    return null
                                }

                                return (
                                    <React.Fragment key={category.categoryIdentifier}>
                                        {/* Category Header Row */}
                                        <tr className={styles.categoryRow}>
                                            <td colSpan="7" className={styles.categoryCell}>
                                                <div className={styles.categoryBadge}>
                                                    <span className={styles.categoryIcon}>
                                                        {(() => {
                                                            const Icon = getCategoryIcon(category.categoryName)
                                                            return <Icon size={15} strokeWidth={1.8} />
                                                        })()}
                                                    </span>
                                                    {category.categoryName}
                                                    <span className={styles.categoryCount}>
                                                        {category.services.length} {category.services.length === 1 ? 'service' : 'services'}
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>

                                        {/* Service Rows */}
                                        {category.services.map((service) => (
                                            <tr key={service.identifier}>
                                                <td className={styles.checkboxCell}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSelectService(service.identifier)}
                                                        className={styles.iconCheckbox}
                                                        aria-pressed={selectedServices.has(service.identifier)}
                                                        aria-label={`Select ${service.serviceName}`}
                                                    >
                                                        {selectedServices.has(service.identifier) ? (
                                                            <CheckSquare size={16} strokeWidth={1.9} />
                                                        ) : (
                                                            <Square size={16} strokeWidth={1.9} />
                                                        )}
                                                    </button>
                                                </td>
                                                <td className={styles.serviceNameCell}>
                                                    <input
                                                        type="text"
                                                        className={styles.input}
                                                        value={getServiceFieldValue(service, 'serviceName')}
                                                        onChange={(e) => handleInputChange(
                                                            service.identifier, 
                                                            'serviceName', 
                                                            e.target.value
                                                        )}
                                                    />
                                                </td>
                                                <td className={styles.priceCell}>
                                                    <input
                                                        type="number"
                                                        className={`${styles.input} ${styles.inputNumber}`}
                                                        value={getServiceFieldValue(service, 'basePrice')}
                                                        onChange={(e) => handleInputChange(
                                                            service.identifier, 
                                                            'basePrice', 
                                                            e.target.value
                                                        )}
                                                    />
                                                </td>
                                                <td className={styles.durationCell}>
                                                    <input
                                                        type="number"
                                                        className={`${styles.input} ${styles.inputNumber}`}
                                                        value={getServiceFieldValue(service, 'serviceTime')}
                                                        placeholder="—"
                                                        onChange={(e) => handleInputChange(
                                                            service.identifier, 
                                                            'serviceTime', 
                                                            e.target.value
                                                        )}
                                                    />
                                                </td>
                                                <td className={styles.hsnCell}>
                                                    <input
                                                        type="text"
                                                        className={styles.input}
                                                        value={getServiceFieldValue(service, 'hsnCode')}
                                                        placeholder="—"
                                                        onChange={(e) => handleInputChange(
                                                            service.identifier, 
                                                            'hsnCode', 
                                                            e.target.value
                                                        )}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className={`${styles.input} ${styles.inputNumber}`}
                                                        value={getServiceFieldValue(service, 'offerPercentage')}
                                                        placeholder="—"
                                                        min="0"
                                                        max="100"
                                                        onChange={(e) => handleInputChange(
                                                            service.identifier, 
                                                            'offerPercentage', 
                                                            e.target.value
                                                        )}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className={styles.input}
                                                        value={getServiceFieldValue(service, 'description')}
                                                        placeholder="—"
                                                        onChange={(e) => handleInputChange(
                                                            service.identifier, 
                                                            'description', 
                                                            e.target.value
                                                        )}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default StorePriceListBulkAction